/**
 * Elite Batch 15 Part 2 — Workflow Templates 1368–1387
 * Meeting notes, scheduling, transcripts, milestones, morning briefings,
 * multi-source reconciliation, multi-step approvals, task aggregation,
 * multi-channel alerts, NPS surveys, order notifications, user provisioning,
 * offer letters, office supplies, fraud detection, outbound sequences,
 * PDF Q&A, and PO approval workflows.
 *
 * DESIGN RULES (inherited from prebuiltWorkflows.ts):
 * 1. Every variable MUST come from a real API response field or AI-generated output
 * 2. No magic variables — if a condition checks something, an upstream node must produce it
 * 3. Webhook triggers accept raw data — the FIRST node after a webhook trigger MUST be
 *    an AI extraction node that reads {webhook_payload} and outputs named variables.
 *    NEVER assume webhook payloads have specific field names (no {email}, {name}, etc.)
 * 4. Every condition has a REAL value (never blank, never "?")
 * 5. Services are pre-selected — user never sees "pick a service"
 * 6. Output variable names use the actual API response field paths
 * 7. Trigger inputFields should be EMPTY for webhooks — the AI extraction node declares outputs
 * 8. AI prompts for extraction should say "find the X regardless of format" to handle variations
 */

function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}
function smsNode(
  id: string,
  label: string,
  y: number,
  to: string,
  message: string,
  x = 80,
): any {
  return n(id, "sendSMS", label, y, { sendSmsDetails: { to, message } }, x);
}
function sheetsNode(
  id: string,
  label: string,
  y: number,
  sheetTab: string,
  values: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/${sheetTab}!A1:append?valueInputOption=USER_ENTERED`,
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "values", value: values }],
        variables: [
          {
            name: "updates.updatedRows",
            customName: "rows_logged",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function hubspotGetNode(
  id: string,
  label: string,
  y: number,
  endpoint: string,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "GET",
        url: `https://api.hubapi.com/${endpoint}`,
        headers: [
          { key: "Authorization", value: "Bearer {hubspot_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [],
        variables: vars,
      },
    },
    x,
  );
}
function hubspotPostNode(
  id: string,
  label: string,
  y: number,
  endpoint: string,
  bodyFields: Array<{ key: string; value: string }>,
  outputs: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://api.hubapi.com/${endpoint}`,
        headers: [
          { key: "Authorization", value: "Bearer {hubspot_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: bodyFields,
        variables: outputs,
      },
    },
    x,
  );
}

// =============================================================================
// ELITE BATCH 15 PART 2 — TEMPLATES 1368–1387
// =============================================================================

export const ELITE_BATCH15_PART2: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ===================================================================
  // 1368. MEETING NOTES GENERATOR (8 nodes)
  // ===================================================================

  "Meeting Notes Generator": {
    summary:
      "Receives a meeting recording completion webhook, uses AI to extract meeting metadata, downloads the transcript via API, generates structured meeting notes with AI, selects the appropriate document template via runCode, routes through an approval gate for draft review, publishes the final document to Google Docs, and sends a distribution notification email to all attendees.",
    nodes: [
      trigger("Webhook: Meeting recording complete", "webhook_app", {
        source: "meeting_platform",
        event: "recording.completed",
        howItStarts:
          "Fires when a meeting recording finishes processing, including the recording URL, meeting ID, participants, duration, and any auto-generated transcript reference",
        setup:
          "Configure webhook from Zoom, Teams, or Google Meet. Set OpenAI API key for AI extraction and notes generation. Provide Google Docs API credentials for document publishing. Configure email for distribution.",
        inputFields: [],
      }),

      // 2. AI extract meeting data
      aiNode(
        "2",
        "AI Extract Meeting Metadata",
        280,
        "You are a meeting data extractor. Parse the raw webhook payload from a recording completion event. Extract meeting title, date, duration, host, participants list, recording URL, transcript URL, meeting ID, and any agenda items regardless of payload structure. Respond JSON: {meeting_title: string, meeting_date: string, duration_minutes: number, host_name: string, host_email: string, participants: string, participant_emails: string, recording_url: string, transcript_url: string, meeting_id: string, agenda_items: string}",
        "Raw webhook payload: {webhook_payload}. Extract all meeting metadata regardless of field naming conventions.",
        [
          { field: "meeting_title", as: "meeting_title", type: "string" },
          { field: "meeting_date", as: "meeting_date", type: "string" },
          { field: "duration_minutes", as: "duration_minutes", type: "number" },
          { field: "host_name", as: "host_name", type: "string" },
          { field: "host_email", as: "host_email", type: "string" },
          { field: "participants", as: "participants", type: "string" },
          {
            field: "participant_emails",
            as: "participant_emails",
            type: "string",
          },
          { field: "recording_url", as: "recording_url", type: "string" },
          { field: "transcript_url", as: "transcript_url", type: "string" },
          { field: "meeting_id", as: "meeting_id", type: "string" },
          { field: "agenda_items", as: "agenda_items", type: "string" },
        ],
      ),

      // 3. Transcript download API
      n("3", "hitApi", "Download Meeting Transcript", 520, {
        hitApiDetails: {
          method: "GET",
          url: "{transcript_url}",
          headers: [
            { key: "Authorization", value: "Bearer {meeting_platform_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "transcript",
              customName: "full_transcript",
              type: "string",
            },
            {
              name: "duration",
              customName: "transcript_duration",
              type: "string",
            },
          ],
        },
      }),

      // 4. AI notes generation
      aiNode(
        "4",
        "AI Generate Structured Meeting Notes",
        760,
        "You are an expert meeting notes writer. Given a full meeting transcript, produce comprehensive structured notes including: executive summary, key discussion points with speaker attributions, decisions made, action items with owners and deadlines, open questions, and next steps. Be thorough but concise. Respond JSON: {executive_summary: string, key_discussions: string, decisions_made: string, action_items: string, open_questions: string, next_steps: string, notes_quality: string, word_count: number}",
        "Meeting: {meeting_title} on {meeting_date}. Duration: {duration_minutes} min. Host: {host_name}. Participants: {participants}. Agenda: {agenda_items}. Full transcript: {full_transcript}. Generate comprehensive structured meeting notes.",
        [
          {
            field: "executive_summary",
            as: "executive_summary",
            type: "string",
          },
          { field: "key_discussions", as: "key_discussions", type: "string" },
          { field: "decisions_made", as: "decisions_made", type: "string" },
          { field: "action_items", as: "action_items", type: "string" },
          { field: "open_questions", as: "open_questions", type: "string" },
          { field: "next_steps", as: "next_steps", type: "string" },
          { field: "notes_quality", as: "notes_quality", type: "string" },
          { field: "word_count", as: "word_count", type: "number" },
        ],
      ),

      // 5. Template selection runCode
      n("5", "runCode", "Select Document Template and Format Notes", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Select template based on meeting type and format final document
const title = (vars.meeting_title || "").toLowerCase();
let templateId = "general_meeting";
if (title.includes("standup") || title.includes("daily")) templateId = "standup";
else if (title.includes("sprint") || title.includes("retro")) templateId = "sprint_ceremony";
else if (title.includes("1:1") || title.includes("one-on-one")) templateId = "one_on_one";
else if (title.includes("board") || title.includes("executive")) templateId = "executive_meeting";
else if (title.includes("client") || title.includes("customer")) templateId = "client_meeting";
const docBody = "<h1>" + (vars.meeting_title || "Meeting Notes") + "</h1>" +
  "<p><strong>Date:</strong> " + (vars.meeting_date || "N/A") + " | <strong>Duration:</strong> " + (vars.duration_minutes || "0") + " min</p>" +
  "<p><strong>Host:</strong> " + (vars.host_name || "N/A") + " | <strong>Participants:</strong> " + (vars.participants || "N/A") + "</p>" +
  "<hr><h2>Executive Summary</h2><p>" + (vars.executive_summary || "N/A") + "</p>" +
  "<h2>Key Discussions</h2><p>" + (vars.key_discussions || "N/A") + "</p>" +
  "<h2>Decisions Made</h2><p>" + (vars.decisions_made || "None") + "</p>" +
  "<h2>Action Items</h2><p>" + (vars.action_items || "None") + "</p>" +
  "<h2>Open Questions</h2><p>" + (vars.open_questions || "None") + "</p>" +
  "<h2>Next Steps</h2><p>" + (vars.next_steps || "None") + "</p>" +
  "<p><em>Recording: " + (vars.recording_url || "N/A") + "</em></p>";
return { template_id: templateId, document_body: docBody, doc_title: (vars.meeting_title || "Meeting Notes") + " — " + (vars.meeting_date || ""), ready_for_review: "yes" };`,
          outputVariables: [
            { name: "template_id", type: "string" },
            { name: "document_body", type: "string" },
            { name: "doc_title", type: "string" },
            { name: "ready_for_review", type: "string" },
          ],
        },
      }),

      // 6. Draft review approval gate
      n("6", "approvalGate", "Review Meeting Notes Draft", 1240, {
        approvalGateData: {
          message:
            "Meeting Notes Review — {doc_title}\n\nTemplate: {template_id}\nQuality: {notes_quality}\nWord Count: {word_count}\n\nExecutive Summary:\n{executive_summary}\n\nAction Items:\n{action_items}\n\nDecisions:\n{decisions_made}\n\nPlease review and approve for publishing to Google Docs and distribution to {participant_emails}.",
        },
      }),

      // 7. Google Docs publisher API
      n("7", "hitApi", "Publish Notes to Google Docs", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://docs.googleapis.com/v1/documents",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "title", value: "{doc_title}" },
            { key: "body", value: "{document_body}" },
          ],
          variables: [
            { name: "documentId", customName: "doc_id", type: "string" },
            { name: "revisionId", customName: "revision_id", type: "string" },
          ],
        },
      }),

      // 8. Distribution notification email
      emailNode(
        "8",
        "Email Meeting Notes to Attendees",
        1720,
        "{participant_emails}",
        "Meeting Notes: {meeting_title} — {meeting_date}",
        "<h2>Meeting Notes Available</h2><p>The notes for <strong>{meeting_title}</strong> ({meeting_date}) are now available.</p><h3>Executive Summary</h3><p>{executive_summary}</p><h3>Action Items</h3><p>{action_items}</p><h3>Next Steps</h3><p>{next_steps}</p><p><a href='https://docs.google.com/document/d/{doc_id}'>View Full Notes in Google Docs</a></p><p>Recording: {recording_url}</p>",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1369. MEETING NOTES TO GOOGLE DOCS SAVER (7 nodes)
  // ===================================================================

  "Meeting Notes to Google Docs Saver": {
    summary:
      "Receives meeting notes via webhook, processes content with AI to extract structured data, maps fields to a Google Docs format with runCode, checks for duplicate documents with a condition, creates the Google Doc via API, sets sharing permissions, and sends a confirmation notification to Slack.",
    nodes: [
      trigger("Webhook: Meeting notes received", "webhook_app", {
        source: "notes_system",
        event: "notes.submitted",
        howItStarts:
          "Fires when meeting notes are submitted from any source — a note-taking app, manual form, or automated transcript — including the content, metadata, and intended recipients",
        setup:
          "Configure webhook from your notes application or form. Set OpenAI API key for content processing. Provide Google Docs and Drive API credentials. Set Slack webhook URL for confirmations.",
        inputFields: [],
      }),

      // 2. AI content processor
      aiNode(
        "2",
        "AI Process and Structure Notes Content",
        280,
        "You are a meeting notes processor. Extract and structure the raw notes content from the webhook payload. Identify the meeting title, date, author, attendees, the full notes body, tags/categories, and any existing document ID that might indicate a duplicate. Respond JSON: {meeting_title: string, meeting_date: string, author_name: string, author_email: string, attendees: string, attendee_emails: string, notes_body: string, category: string, existing_doc_id: string, has_action_items: string}",
        "Raw webhook payload: {webhook_payload}. Extract all notes content and metadata regardless of payload structure.",
        [
          { field: "meeting_title", as: "meeting_title", type: "string" },
          { field: "meeting_date", as: "meeting_date", type: "string" },
          { field: "author_name", as: "author_name", type: "string" },
          { field: "author_email", as: "author_email", type: "string" },
          { field: "attendees", as: "attendees", type: "string" },
          { field: "attendee_emails", as: "attendee_emails", type: "string" },
          { field: "notes_body", as: "notes_body", type: "string" },
          { field: "category", as: "notes_category", type: "string" },
          { field: "existing_doc_id", as: "existing_doc_id", type: "string" },
          { field: "has_action_items", as: "has_action_items", type: "string" },
        ],
      ),

      // 3. Field mapper runCode
      n("3", "runCode", "Map Fields to Google Docs Format", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Format notes content for Google Docs creation
const title = (vars.meeting_title || "Untitled Meeting") + " — " + (vars.meeting_date || new Date().toISOString().split("T")[0]);
const docContent = "<h1>" + (vars.meeting_title || "Meeting Notes") + "</h1>" +
  "<p><strong>Date:</strong> " + (vars.meeting_date || "N/A") + "</p>" +
  "<p><strong>Author:</strong> " + (vars.author_name || "Unknown") + "</p>" +
  "<p><strong>Attendees:</strong> " + (vars.attendees || "N/A") + "</p>" +
  "<p><strong>Category:</strong> " + (vars.notes_category || "General") + "</p>" +
  "<hr>" + (vars.notes_body || "No content provided.");
const isDuplicate = (vars.existing_doc_id || "").length > 3 ? "yes" : "no";
const folderMap = { engineering: "eng_folder_id", sales: "sales_folder_id", product: "product_folder_id", general: "general_folder_id" };
const cat = (vars.notes_category || "general").toLowerCase();
const folderId = folderMap[cat] || "general_folder_id";
return { doc_title: title, formatted_content: docContent, is_duplicate: isDuplicate, target_folder_id: folderId, created_at: new Date().toISOString() };`,
          outputVariables: [
            { name: "doc_title", type: "string" },
            { name: "formatted_content", type: "string" },
            { name: "is_duplicate", type: "string" },
            { name: "target_folder_id", type: "string" },
            { name: "created_at", type: "string" },
          ],
        },
      }),

      // 4. Conflict detection condition
      n("4", "condition", "Duplicate Document Detected?", 760, {
        conditionDetails: {
          conditions: [
            { variable: "is_duplicate", operator: "eq", value: "no" },
          ],
        },
      }),

      // 5. Google Docs creator API (True path — no duplicate)
      n("5", "hitApi", "Create Google Doc with Notes", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://docs.googleapis.com/v1/documents",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "title", value: "{doc_title}" },
            { key: "body", value: "{formatted_content}" },
          ],
          variables: [
            {
              name: "documentId",
              customName: "created_doc_id",
              type: "string",
            },
            { name: "revisionId", customName: "doc_revision", type: "string" },
          ],
        },
      }),

      // 6. Sharing permissions API
      n("6", "hitApi", "Set Sharing Permissions for Attendees", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://www.googleapis.com/drive/v3/files/{created_doc_id}/permissions",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "role", value: "writer" },
            { key: "type", value: "user" },
            { key: "emailAddress", value: "{attendee_emails}" },
          ],
          variables: [
            { name: "id", customName: "permission_id", type: "string" },
            { name: "role", customName: "granted_role", type: "string" },
          ],
        },
      }),

      // 7. Confirmation Slack
      slackNode(
        "7",
        "Slack Confirmation: Notes Saved",
        1480,
        "Meeting notes saved to Google Docs:\\n*Title:* {doc_title}\\n*Author:* {author_name}\\n*Category:* {notes_category}\\n*Attendees:* {attendees}\\n*Doc Link:* https://docs.google.com/document/d/{created_doc_id}\\n*Created:* {created_at}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "7", "output_2"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1370. MEETING SCHEDULER BOT (8 nodes)
  // ===================================================================

  "Meeting Scheduler Bot": {
    summary:
      "Receives booking requests via webhook, extracts scheduling data with AI, checks Google Calendar availability, uses AI to suggest optimal time slots, presents options via Slack for selection, creates the calendar event upon confirmation, sends participant notification emails, and schedules a reminder with a delay node.",
    nodes: [
      trigger("Webhook: Meeting booking request", "webhook_app", {
        source: "scheduling_system",
        event: "meeting.booking_requested",
        howItStarts:
          "Fires when someone requests to schedule a meeting, including desired participants, topic, preferred dates/times, duration, and urgency level",
        setup:
          "Configure webhook from your scheduling form or Slack bot. Set OpenAI API key. Provide Google Calendar API credentials. Set Slack webhook for time slot presentation. Configure email for participant notifications.",
        inputFields: [],
      }),

      // 2. AI extract scheduling data
      aiNode(
        "2",
        "AI Extract Scheduling Request Details",
        280,
        "You are a scheduling assistant. Parse the raw webhook payload from a meeting booking request. Extract the meeting topic, requested participants with their emails, preferred date range, preferred time range, required duration, urgency, meeting type (in-person/virtual), and any special requirements. Respond JSON: {meeting_topic: string, organizer_email: string, organizer_name: string, participant_emails: string, participant_names: string, preferred_date_start: string, preferred_date_end: string, preferred_time_start: string, preferred_time_end: string, duration_minutes: number, urgency: string, meeting_type: string, special_requirements: string}",
        "Raw webhook payload: {webhook_payload}. Extract all scheduling parameters regardless of payload format.",
        [
          { field: "meeting_topic", as: "meeting_topic", type: "string" },
          { field: "organizer_email", as: "organizer_email", type: "string" },
          { field: "organizer_name", as: "organizer_name", type: "string" },
          {
            field: "participant_emails",
            as: "participant_emails",
            type: "string",
          },
          {
            field: "participant_names",
            as: "participant_names",
            type: "string",
          },
          {
            field: "preferred_date_start",
            as: "preferred_date_start",
            type: "string",
          },
          {
            field: "preferred_date_end",
            as: "preferred_date_end",
            type: "string",
          },
          {
            field: "preferred_time_start",
            as: "preferred_time_start",
            type: "string",
          },
          {
            field: "preferred_time_end",
            as: "preferred_time_end",
            type: "string",
          },
          { field: "duration_minutes", as: "duration_minutes", type: "number" },
          { field: "urgency", as: "urgency", type: "string" },
          { field: "meeting_type", as: "meeting_type", type: "string" },
          {
            field: "special_requirements",
            as: "special_requirements",
            type: "string",
          },
        ],
      ),

      // 3. Google Calendar availability check
      n("3", "hitApi", "Check Google Calendar Availability", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://www.googleapis.com/calendar/v3/freeBusy",
          headers: [
            { key: "Authorization", value: "Bearer {google_calendar_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "timeMin",
              value: "{preferred_date_start}T{preferred_time_start}:00Z",
            },
            {
              key: "timeMax",
              value: "{preferred_date_end}T{preferred_time_end}:00Z",
            },
            {
              key: "items",
              value:
                '[{"id":"{organizer_email}"},{"id":"{participant_emails}"}]',
            },
          ],
          variables: [
            {
              name: "calendars",
              customName: "calendar_busy_data",
              type: "string",
            },
            { name: "kind", customName: "response_kind", type: "string" },
          ],
        },
      }),

      // 4. AI optimal time suggester
      aiNode(
        "4",
        "AI Suggest Optimal Meeting Times",
        760,
        "You are a scheduling optimizer. Given calendar busy/free data and meeting constraints, identify the top 3 available time slots that work for all participants. Prefer morning slots for urgency, avoid lunch hours, respect timezone norms. Respond JSON: {slot_1: string, slot_1_date: string, slot_1_time: string, slot_2: string, slot_2_date: string, slot_2_time: string, slot_3: string, slot_3_date: string, slot_3_time: string, recommendation: string, conflict_notes: string}",
        "Meeting: {meeting_topic}. Duration: {duration_minutes} min. Type: {meeting_type}. Urgency: {urgency}. Preferred window: {preferred_date_start} to {preferred_date_end}, {preferred_time_start}-{preferred_time_end}. Calendar data: {calendar_busy_data}. Find the best 3 available slots.",
        [
          { field: "slot_1", as: "slot_1", type: "string" },
          { field: "slot_1_date", as: "slot_1_date", type: "string" },
          { field: "slot_1_time", as: "slot_1_time", type: "string" },
          { field: "slot_2", as: "slot_2", type: "string" },
          { field: "slot_3", as: "slot_3", type: "string" },
          { field: "recommendation", as: "ai_recommendation", type: "string" },
          { field: "conflict_notes", as: "conflict_notes", type: "string" },
        ],
      ),

      // 5. Time slot presenter Slack
      slackNode(
        "5",
        "Present Time Slots via Slack",
        1000,
        "Meeting Scheduling Request from {organizer_name}:\\n*Topic:* {meeting_topic}\\n*Duration:* {duration_minutes} min ({meeting_type})\\n*Participants:* {participant_names}\\n\\n*Available Slots:*\\n1. {slot_1}\\n2. {slot_2}\\n3. {slot_3}\\n\\n*AI Recommendation:* {ai_recommendation}\\n*Notes:* {conflict_notes}\\n\\nReply with slot number to confirm.",
      ),

      // 6. Booking confirmation Google Calendar
      n("6", "hitApi", "Create Calendar Event for Selected Slot", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://www.googleapis.com/calendar/v3/calendars/primary/events",
          headers: [
            { key: "Authorization", value: "Bearer {google_calendar_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "summary", value: "{meeting_topic}" },
            {
              key: "start",
              value:
                '{"dateTime":"{slot_1_date}T{slot_1_time}:00","timeZone":"UTC"}',
            },
            {
              key: "end",
              value:
                '{"dateTime":"{slot_1_date}T{slot_1_time}:00","timeZone":"UTC"}',
            },
            {
              key: "attendees",
              value:
                '[{"email":"{organizer_email}"},{"email":"{participant_emails}"}]',
            },
            {
              key: "description",
              value: "{meeting_topic} — {special_requirements}",
            },
          ],
          variables: [
            { name: "id", customName: "event_id", type: "string" },
            { name: "htmlLink", customName: "event_link", type: "string" },
            { name: "status", customName: "event_status", type: "string" },
          ],
        },
      }),

      // 7. Participant notification email
      emailNode(
        "7",
        "Email Meeting Confirmation to Participants",
        1480,
        "{participant_emails}",
        "Meeting Scheduled: {meeting_topic} — {slot_1}",
        "<h2>Meeting Confirmed</h2><p>Your meeting has been scheduled:</p><ul><li><strong>Topic:</strong> {meeting_topic}</li><li><strong>When:</strong> {slot_1}</li><li><strong>Duration:</strong> {duration_minutes} minutes</li><li><strong>Type:</strong> {meeting_type}</li><li><strong>Organizer:</strong> {organizer_name}</li><li><strong>Participants:</strong> {participant_names}</li></ul><p>{special_requirements}</p><p><a href='{event_link}'>View in Google Calendar</a></p>",
      ),

      // 8. Reminder scheduler delay
      n("8", "delayAction", "Schedule 15-minute Pre-Meeting Reminder", 1720, {
        delayAction: { time: 15, timeFormat: "minutes" },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1371. MEETING TRANSCRIPT SUMMARIZER AGENT (8 nodes)
  // ===================================================================

  "Meeting Transcript Summarizer Agent": {
    summary:
      "Receives a transcript via webhook, extracts the full text with AI, preprocesses and cleans the transcript with runCode, generates multi-level summaries (executive, detailed, action-oriented) with AI, extracts key metrics with runCode, analyzes discussion trends with runCode, formats the final report with runCode, and distributes via email.",
    nodes: [
      trigger("Webhook: Meeting transcript available", "webhook_app", {
        source: "transcription_service",
        event: "transcript.ready",
        howItStarts:
          "Fires when a meeting transcript is finalized and ready for processing, including the raw transcript text, speaker labels, timestamps, and meeting metadata",
        setup:
          "Configure webhook from your transcription service (Otter.ai, Rev, Fireflies, etc.). Set OpenAI API key for AI summarization. Configure email delivery for report distribution.",
        inputFields: [],
      }),

      // 2. AI extract transcript data
      aiNode(
        "2",
        "AI Extract Transcript and Metadata",
        280,
        "You are a transcript parser. Extract the full meeting transcript text, speaker names, meeting title, date, duration, total word count, and distribution list from the raw webhook payload. Respond JSON: {meeting_title: string, meeting_date: string, duration_minutes: number, speakers: string, transcript_text: string, word_count: number, distribution_emails: string, language: string}",
        "Raw webhook payload: {webhook_payload}. Extract the complete transcript and all metadata regardless of payload format.",
        [
          { field: "meeting_title", as: "meeting_title", type: "string" },
          { field: "meeting_date", as: "meeting_date", type: "string" },
          { field: "duration_minutes", as: "duration_minutes", type: "number" },
          { field: "speakers", as: "speakers", type: "string" },
          { field: "transcript_text", as: "transcript_text", type: "string" },
          { field: "word_count", as: "word_count", type: "number" },
          {
            field: "distribution_emails",
            as: "distribution_emails",
            type: "string",
          },
          { field: "language", as: "transcript_language", type: "string" },
        ],
      ),

      // 3. Transcript preprocessor runCode
      n("3", "runCode", "Preprocess and Clean Transcript", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Clean and preprocess the transcript for AI summarization
const raw = vars.transcript_text || "";
// Remove timestamps like [00:12:34] or (12:34)
const cleaned = raw.replace(/\\[\\d{2}:\\d{2}(:\\d{2})?\\]/g, "").replace(/\\(\\d{2}:\\d{2}(:\\d{2})?\\)/g, "");
// Normalize speaker labels
const normalized = cleaned.replace(/\\n{3,}/g, "\\n\\n").trim();
const words = normalized.split(/\\s+/).length;
// Chunk if very long (>10000 words)
const needsChunking = words > 10000 ? "yes" : "no";
const chunks = [];
if (words > 10000) {
  const sentences = normalized.split(/(?<=[.!?])\\s+/);
  let chunk = "";
  for (const s of sentences) {
    if ((chunk + " " + s).split(/\\s+/).length > 5000) {
      chunks.push(chunk.trim());
      chunk = s;
    } else {
      chunk += " " + s;
    }
  }
  if (chunk.trim()) chunks.push(chunk.trim());
}
return {
  cleaned_transcript: normalized,
  processed_word_count: words,
  needs_chunking: needsChunking,
  chunk_count: chunks.length || 1,
  first_chunk: chunks[0] || normalized,
  processing_timestamp: new Date().toISOString()
};`,
          outputVariables: [
            { name: "cleaned_transcript", type: "string" },
            { name: "processed_word_count", type: "number" },
            { name: "needs_chunking", type: "string" },
            { name: "chunk_count", type: "number" },
            { name: "first_chunk", type: "string" },
            { name: "processing_timestamp", type: "string" },
          ],
        },
      }),

      // 4. AI multi-level summarizer
      aiNode(
        "4",
        "AI Generate Multi-Level Summaries",
        760,
        "You are an expert meeting summarizer. Generate three levels of summary: 1) Executive summary (2-3 sentences for leadership), 2) Detailed summary (comprehensive paragraph covering all topics), 3) Action-oriented summary (focused on decisions, action items with owners, deadlines, blockers). Also extract sentiment, engagement level, and key quotes. Respond JSON: {executive_summary: string, detailed_summary: string, action_summary: string, decisions: string, action_items: string, key_quotes: string, overall_sentiment: string, engagement_level: string, topics_covered: string}",
        "Meeting: {meeting_title} ({meeting_date}), {duration_minutes} min. Speakers: {speakers}. Transcript ({processed_word_count} words): {cleaned_transcript}. Generate all three summary levels.",
        [
          {
            field: "executive_summary",
            as: "executive_summary",
            type: "string",
          },
          { field: "detailed_summary", as: "detailed_summary", type: "string" },
          { field: "action_summary", as: "action_summary", type: "string" },
          { field: "decisions", as: "decisions", type: "string" },
          { field: "action_items", as: "action_items", type: "string" },
          { field: "key_quotes", as: "key_quotes", type: "string" },
          {
            field: "overall_sentiment",
            as: "overall_sentiment",
            type: "string",
          },
          { field: "engagement_level", as: "engagement_level", type: "string" },
          { field: "topics_covered", as: "topics_covered", type: "string" },
        ],
      ),

      // 5. Key metrics extractor runCode
      n("5", "runCode", "Extract Key Meeting Metrics", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Calculate key meeting metrics
const duration = parseInt(vars.duration_minutes || "0", 10);
const wordCount = parseInt(vars.processed_word_count || "0", 10);
const speakers = (vars.speakers || "").split(",").map(s => s.trim()).filter(Boolean);
const actionItems = (vars.action_items || "").split(/[;\\n]/).filter(a => a.trim().length > 5);
const decisions = (vars.decisions || "").split(/[;\\n]/).filter(d => d.trim().length > 5);
const wordsPerMinute = duration > 0 ? Math.round(wordCount / duration) : 0;
const efficiency = actionItems.length + decisions.length > 0 ? "productive" : "discussion_only";
const meetingScore = Math.min(100, Math.round(((actionItems.length * 15) + (decisions.length * 20) + (speakers.length * 5)) * (duration > 0 ? 60 / duration : 1)));
return {
  speaker_count: speakers.length,
  action_item_count: actionItems.length,
  decision_count: decisions.length,
  words_per_minute: wordsPerMinute,
  meeting_efficiency: efficiency,
  productivity_score: meetingScore,
  metrics_generated: "yes"
};`,
          outputVariables: [
            { name: "speaker_count", type: "number" },
            { name: "action_item_count", type: "number" },
            { name: "decision_count", type: "number" },
            { name: "words_per_minute", type: "number" },
            { name: "meeting_efficiency", type: "string" },
            { name: "productivity_score", type: "number" },
            { name: "metrics_generated", type: "string" },
          ],
        },
      }),

      // 6. Trend analyzer runCode
      n("6", "runCode", "Analyze Discussion Trends", 1240, {
        runCodeDetails: {
          language: "javascript",
          code: `// Analyze discussion patterns and trends
const topics = (vars.topics_covered || "").split(",").map(t => t.trim()).filter(Boolean);
const sentiment = (vars.overall_sentiment || "neutral").toLowerCase();
const engagement = (vars.engagement_level || "medium").toLowerCase();
const score = parseInt(vars.productivity_score || "50", 10);
let trendInsight = "";
if (score >= 75) trendInsight = "Highly productive meeting with clear outcomes";
else if (score >= 50) trendInsight = "Moderately productive — consider tighter agenda next time";
else trendInsight = "Low productivity — recommend shorter format or async updates";
const topicDensity = topics.length > 0 && parseInt(vars.duration_minutes || "0", 10) > 0 ? (topics.length / (parseInt(vars.duration_minutes || "1", 10) / 15)).toFixed(1) : "N/A";
return {
  topic_count: topics.length,
  topic_density: topicDensity + " topics per 15 min",
  sentiment_trend: sentiment,
  engagement_trend: engagement,
  trend_insight: trendInsight,
  improvement_suggestion: score < 50 ? "Consider using async standup updates for status-only meetings" : "Keep current meeting format"
};`,
          outputVariables: [
            { name: "topic_count", type: "number" },
            { name: "topic_density", type: "string" },
            { name: "sentiment_trend", type: "string" },
            { name: "engagement_trend", type: "string" },
            { name: "trend_insight", type: "string" },
            { name: "improvement_suggestion", type: "string" },
          ],
        },
      }),

      // 7. Report formatter runCode
      n("7", "runCode", "Format Final Summary Report", 1480, {
        runCodeDetails: {
          language: "javascript",
          code: `// Build final formatted report HTML
const report = "<h1>Meeting Summary Report</h1>" +
  "<p><strong>" + (vars.meeting_title || "Meeting") + "</strong> — " + (vars.meeting_date || "N/A") + " (" + (vars.duration_minutes || "0") + " min)</p>" +
  "<p>Speakers: " + (vars.speakers || "N/A") + " (" + (vars.speaker_count || "0") + " participants)</p>" +
  "<hr><h2>Executive Summary</h2><p>" + (vars.executive_summary || "N/A") + "</p>" +
  "<h2>Detailed Summary</h2><p>" + (vars.detailed_summary || "N/A") + "</p>" +
  "<h2>Action Items (" + (vars.action_item_count || "0") + ")</h2><p>" + (vars.action_items || "None") + "</p>" +
  "<h2>Decisions (" + (vars.decision_count || "0") + ")</h2><p>" + (vars.decisions || "None") + "</p>" +
  "<h2>Key Quotes</h2><p>" + (vars.key_quotes || "None captured") + "</p>" +
  "<h2>Meeting Analytics</h2>" +
  "<ul><li>Productivity Score: " + (vars.productivity_score || "0") + "/100</li>" +
  "<li>Efficiency: " + (vars.meeting_efficiency || "N/A") + "</li>" +
  "<li>Sentiment: " + (vars.sentiment_trend || "N/A") + "</li>" +
  "<li>Engagement: " + (vars.engagement_trend || "N/A") + "</li>" +
  "<li>Topics Covered: " + (vars.topic_count || "0") + " (" + (vars.topic_density || "N/A") + ")</li></ul>" +
  "<p><em>Insight: " + (vars.trend_insight || "N/A") + "</em></p>" +
  "<p><em>Suggestion: " + (vars.improvement_suggestion || "N/A") + "</em></p>";
return { final_report: report, report_subject: "Meeting Summary: " + (vars.meeting_title || "Meeting") + " — " + (vars.meeting_date || "Today") };`,
          outputVariables: [
            { name: "final_report", type: "string" },
            { name: "report_subject", type: "string" },
          ],
        },
      }),

      // 8. Distribution email
      emailNode(
        "8",
        "Email Summary Report to Attendees",
        1720,
        "{distribution_emails}",
        "{report_subject}",
        "{final_report}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1372. MILESTONE CELEBRATION NOTIFIER (6 nodes)
  // ===================================================================

  "Milestone Celebration Notifier": {
    summary:
      "Receives milestone events via webhook, extracts milestone data with AI, validates the milestone against criteria with a condition, generates personalized celebration content with AI, sends a celebration message to Slack, and delivers a recognition email to the achiever and their team.",
    nodes: [
      trigger("Webhook: Milestone event achieved", "webhook_app", {
        source: "achievement_system",
        event: "milestone.achieved",
        howItStarts:
          "Fires when a team member or team achieves a milestone — project completion, sales target, anniversary, customer count, or any tracked KPI hitting its goal",
        setup:
          "Configure webhook from your project management, CRM, or HR system. Set OpenAI API key for content generation. Set Slack webhook URL for celebrations. Configure email for recognition delivery.",
        inputFields: [],
      }),

      // 2. AI extract milestone data
      aiNode(
        "2",
        "AI Extract Milestone Details",
        280,
        "You are a milestone data parser. Extract the milestone type, achievement description, achiever name, achiever email, team, manager email, metric value, target value, date achieved, and significance level from the raw webhook payload. Respond JSON: {milestone_type: string, achievement: string, achiever_name: string, achiever_email: string, team_name: string, manager_email: string, metric_value: string, target_value: string, date_achieved: string, significance: string, is_valid_milestone: string}",
        "Raw webhook payload: {webhook_payload}. Extract all milestone details regardless of payload structure.",
        [
          { field: "milestone_type", as: "milestone_type", type: "string" },
          { field: "achievement", as: "achievement", type: "string" },
          { field: "achiever_name", as: "achiever_name", type: "string" },
          { field: "achiever_email", as: "achiever_email", type: "string" },
          { field: "team_name", as: "team_name", type: "string" },
          { field: "manager_email", as: "manager_email", type: "string" },
          { field: "metric_value", as: "metric_value", type: "string" },
          { field: "target_value", as: "target_value", type: "string" },
          { field: "date_achieved", as: "date_achieved", type: "string" },
          { field: "significance", as: "significance", type: "string" },
          {
            field: "is_valid_milestone",
            as: "is_valid_milestone",
            type: "string",
          },
        ],
      ),

      // 3. Milestone validator condition
      n("3", "condition", "Is This a Valid Milestone?", 520, {
        conditionDetails: {
          conditions: [
            { variable: "is_valid_milestone", operator: "eq", value: "true" },
          ],
        },
      }),

      // 4. AI celebration content generator (True path)
      aiNode(
        "4",
        "AI Generate Celebration Content",
        760,
        "You are a celebration content creator. Generate warm, enthusiastic, and personalized celebration messages for workplace milestones. Create both a Slack-friendly short message with emojis and a longer email-worthy recognition message. Match the tone to the significance level. Respond JSON: {slack_message: string, email_subject: string, email_body: string, celebration_title: string, fun_fact: string}",
        "Milestone: {milestone_type}. Achievement: {achievement}. Achiever: {achiever_name} from {team_name}. Metric: {metric_value} / Target: {target_value}. Significance: {significance}. Date: {date_achieved}. Generate celebration content.",
        [
          { field: "slack_message", as: "celebration_slack", type: "string" },
          {
            field: "email_subject",
            as: "celebration_email_subject",
            type: "string",
          },
          { field: "email_body", as: "celebration_email_body", type: "string" },
          {
            field: "celebration_title",
            as: "celebration_title",
            type: "string",
          },
          { field: "fun_fact", as: "fun_fact", type: "string" },
        ],
      ),

      // 5. Multi-channel celebration Slack
      slackNode("5", "Post Celebration to Slack", 1000, "{celebration_slack}"),

      // 6. Email recognition
      emailNode(
        "6",
        "Email Recognition to Achiever and Manager",
        1240,
        "{achiever_email},{manager_email}",
        "{celebration_email_subject}",
        "{celebration_email_body}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"),
      e("4", "5"),
      e("5", "6"),
    ],
  },

  // ===================================================================
  // 1373. MORNING BRIEFING BOT (8 nodes)
  // ===================================================================

  "Morning Briefing Bot": {
    summary:
      "Runs on a morning schedule, aggregates today's calendar events from Google Calendar, fetches priority tasks from Asana, pulls market/news data from an API, retrieves team activity from Slack, composes a comprehensive briefing with AI, delivers it via email, and posts a summary to Slack for the team.",
    nodes: [
      trigger("Schedule: Morning briefing compilation", "schedule", {
        schedule: "Every weekday at 7:30 AM",
        howItStarts:
          "Runs every weekday morning to compile a comprehensive briefing including calendar events, priority tasks, market data, and team activity for the day ahead",
        setup:
          "Provide Google Calendar API credentials for event fetching. Set Asana API token for task retrieval. Configure news/market data API endpoint. Set Slack API token for activity fetching. Configure email and Slack webhook for delivery.",
        inputFields: [],
      }),

      // 2. Calendar aggregator Google Calendar API
      n("2", "hitApi", "Fetch Today's Calendar Events", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://www.googleapis.com/calendar/v3/calendars/primary/events?timeMin={today_start}T00:00:00Z&timeMax={today_start}T23:59:59Z&singleEvents=true&orderBy=startTime",
          headers: [
            { key: "Authorization", value: "Bearer {google_calendar_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "items", customName: "calendar_events", type: "string" },
            { name: "summary", customName: "calendar_name", type: "string" },
          ],
        },
      }),

      // 3. Task priority fetcher Asana API
      n("3", "hitApi", "Fetch Priority Tasks from Asana", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://app.asana.com/api/1.0/tasks?project={asana_project_id}&completed_since=now&opt_fields=name,due_on,assignee.name,notes,custom_fields",
          headers: [
            { key: "Authorization", value: "Bearer {asana_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "data", customName: "asana_tasks", type: "string" },
          ],
        },
      }),

      // 4. News / market data API
      n("4", "hitApi", "Fetch Market and Industry News", 760, {
        hitApiDetails: {
          method: "GET",
          url: "https://newsapi.org/v2/top-headlines?category=business&language=en&pageSize=5&apiKey={newsapi_key}",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [],
          variables: [
            { name: "articles", customName: "news_articles", type: "string" },
            { name: "totalResults", customName: "news_count", type: "number" },
          ],
        },
      }),

      // 5. Team activity summary Slack API
      n("5", "hitApi", "Fetch Team Activity from Slack", 1000, {
        hitApiDetails: {
          method: "GET",
          url: "https://slack.com/api/conversations.history?channel={team_channel_id}&limit=20",
          headers: [
            { key: "Authorization", value: "Bearer {slack_bot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "messages", customName: "slack_messages", type: "string" },
            {
              name: "has_more",
              customName: "has_more_messages",
              type: "boolean",
            },
          ],
        },
      }),

      // 6. AI briefing composer
      aiNode(
        "6",
        "AI Compose Morning Briefing",
        1240,
        "You are an executive assistant composing a morning briefing. Synthesize calendar events, priority tasks, market news, and team activity into a clear, actionable daily briefing. Highlight the 3 most important things for today, flag any conflicts or urgent items, and provide a motivational opening. Respond JSON: {briefing_greeting: string, top_3_priorities: string, calendar_summary: string, task_summary: string, news_highlights: string, team_activity_summary: string, conflicts_warnings: string, briefing_html: string}",
        "Today's calendar events: {calendar_events}. Priority tasks from Asana: {asana_tasks}. Business news: {news_articles}. Recent Slack team activity: {slack_messages}. Compose a comprehensive morning briefing.",
        [
          {
            field: "briefing_greeting",
            as: "briefing_greeting",
            type: "string",
          },
          { field: "top_3_priorities", as: "top_3_priorities", type: "string" },
          { field: "calendar_summary", as: "calendar_summary", type: "string" },
          { field: "task_summary", as: "task_summary", type: "string" },
          { field: "news_highlights", as: "news_highlights", type: "string" },
          {
            field: "team_activity_summary",
            as: "team_activity_summary",
            type: "string",
          },
          {
            field: "conflicts_warnings",
            as: "conflicts_warnings",
            type: "string",
          },
          { field: "briefing_html", as: "briefing_html", type: "string" },
        ],
      ),

      // 7. Delivery channel email
      emailNode(
        "7",
        "Email Morning Briefing",
        1480,
        "{user_email}",
        "Morning Briefing — {briefing_greeting}",
        "{briefing_html}",
      ),

      // 8. Interaction handler Slack
      slackNode(
        "8",
        "Post Briefing Summary to Slack",
        1720,
        "Good morning! Here is your daily briefing:\\n\\n*Top 3 Priorities:*\\n{top_3_priorities}\\n\\n*Calendar:* {calendar_summary}\\n*Tasks:* {task_summary}\\n*News:* {news_highlights}\\n\\n*Warnings:* {conflicts_warnings}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1374. MULTI-SOURCE DATA RECONCILIATION AGENT (9 nodes)
  // ===================================================================

  "Multi Source Data Reconciliation Agent": {
    summary:
      "Runs nightly to fetch data from three different sources via API, normalizes field mappings with AI, detects discrepancies with runCode, auto-resolves simple mismatches with runCode, logs the discrepancy report to Google Sheets, and pushes resolution updates to HubSpot CRM.",
    nodes: [
      trigger("Schedule: Nightly data reconciliation", "schedule", {
        schedule: "Every day at 2:00 AM",
        howItStarts:
          "Runs nightly to pull data from three different source systems, normalize and compare records, detect discrepancies, auto-resolve simple mismatches, and report unresolvable conflicts for manual review",
        setup:
          "Configure API credentials for all three data sources. Set OpenAI API key for AI-powered field mapping. Provide Google Sheets spreadsheet ID for discrepancy reporting. Set HubSpot token for resolution updates.",
        inputFields: [],
      }),

      // 2. Source A data fetch API
      n("2", "hitApi", "Fetch Records from Source A (CRM)", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.hubapi.com/crm/v3/objects/contacts?limit=100&properties=email,firstname,lastname,phone,company,lifecyclestage",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "results", customName: "source_a_records", type: "string" },
            { name: "total", customName: "source_a_count", type: "number" },
          ],
        },
      }),

      // 3. Source B data fetch API
      n("3", "hitApi", "Fetch Records from Source B (Billing)", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.stripe.com/v1/customers?limit=100",
          headers: [
            { key: "Authorization", value: "Bearer {stripe_secret_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "data", customName: "source_b_records", type: "string" },
            {
              name: "has_more",
              customName: "source_b_has_more",
              type: "boolean",
            },
          ],
        },
      }),

      // 4. Source C data fetch API
      n("4", "hitApi", "Fetch Records from Source C (Support)", 760, {
        hitApiDetails: {
          method: "GET",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/SupportContacts!A2:F500",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "values", customName: "source_c_records", type: "string" },
            { name: "range", customName: "source_c_range", type: "string" },
          ],
        },
      }),

      // 5. AI field mapping normalization
      aiNode(
        "5",
        "AI Normalize and Map Fields Across Sources",
        1000,
        "You are a data reconciliation expert. Given records from three different data sources (CRM, Billing, Support), map and normalize fields so they can be compared. Use email as the primary matching key. Identify records that exist in all sources, only in some sources, and flag any mismatches in names, phone numbers, or status. Respond JSON: {matched_count: number, source_a_only: number, source_b_only: number, source_c_only: number, mismatched_records: string, normalized_data: string, field_mapping_log: string, reconciliation_status: string}",
        "Source A (CRM — {source_a_count} records): {source_a_records}. Source B (Billing): {source_b_records}. Source C (Support): {source_c_records}. Normalize and compare all records by email key.",
        [
          { field: "matched_count", as: "matched_count", type: "number" },
          { field: "source_a_only", as: "source_a_only", type: "number" },
          { field: "source_b_only", as: "source_b_only", type: "number" },
          { field: "source_c_only", as: "source_c_only", type: "number" },
          {
            field: "mismatched_records",
            as: "mismatched_records",
            type: "string",
          },
          { field: "normalized_data", as: "normalized_data", type: "string" },
          {
            field: "field_mapping_log",
            as: "field_mapping_log",
            type: "string",
          },
          {
            field: "reconciliation_status",
            as: "reconciliation_status",
            type: "string",
          },
        ],
      ),

      // 6. Discrepancy detection runCode
      n("6", "runCode", "Detect and Classify Discrepancies", 1240, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse mismatch data and classify discrepancies
const mismatches = (vars.mismatched_records || "").split("|").filter(m => m.trim().length > 0);
const matched = parseInt(vars.matched_count || "0", 10);
const aOnly = parseInt(vars.source_a_only || "0", 10);
const bOnly = parseInt(vars.source_b_only || "0", 10);
const cOnly = parseInt(vars.source_c_only || "0", 10);
const totalDiscrepancies = mismatches.length + aOnly + bOnly + cOnly;
const autoResolvable = mismatches.filter(m => m.toLowerCase().includes("case") || m.toLowerCase().includes("format") || m.toLowerCase().includes("whitespace"));
const manualReview = mismatches.filter(m => !m.toLowerCase().includes("case") && !m.toLowerCase().includes("format") && !m.toLowerCase().includes("whitespace"));
const healthScore = matched > 0 ? Math.round((matched / (matched + totalDiscrepancies)) * 100) : 0;
return {
  total_discrepancies: totalDiscrepancies,
  auto_resolvable_count: autoResolvable.length,
  auto_resolvable_list: autoResolvable.join(" | ") || "none",
  manual_review_count: manualReview.length,
  manual_review_list: manualReview.join(" | ") || "none",
  missing_in_crm: aOnly,
  missing_in_billing: bOnly,
  missing_in_support: cOnly,
  data_health_score: healthScore,
  reconciliation_date: new Date().toISOString().split("T")[0]
};`,
          outputVariables: [
            { name: "total_discrepancies", type: "number" },
            { name: "auto_resolvable_count", type: "number" },
            { name: "auto_resolvable_list", type: "string" },
            { name: "manual_review_count", type: "number" },
            { name: "manual_review_list", type: "string" },
            { name: "missing_in_crm", type: "number" },
            { name: "missing_in_billing", type: "number" },
            { name: "missing_in_support", type: "number" },
            { name: "data_health_score", type: "number" },
            { name: "reconciliation_date", type: "string" },
          ],
        },
      }),

      // 7. Auto-resolution runCode
      n("7", "runCode", "Auto-Resolve Simple Discrepancies", 1480, {
        runCodeDetails: {
          language: "javascript",
          code: `// Generate resolution payloads for auto-resolvable discrepancies
const autoList = (vars.auto_resolvable_list || "none");
const autoCount = parseInt(vars.auto_resolvable_count || "0", 10);
let resolved = 0;
let resolutionLog = [];
if (autoList !== "none") {
  const items = autoList.split(" | ");
  for (const item of items) {
    resolutionLog.push("RESOLVED: " + item.trim() + " — normalized format");
    resolved++;
  }
}
const remainingManual = parseInt(vars.manual_review_count || "0", 10);
const resolutionSummary = "Auto-resolved: " + resolved + "/" + autoCount + ". Remaining for manual review: " + remainingManual;
return {
  resolved_count: resolved,
  resolution_log: resolutionLog.join(" | ") || "none",
  resolution_summary: resolutionSummary,
  remaining_manual: remainingManual,
  needs_manual_update: remainingManual > 0 ? "yes" : "no"
};`,
          outputVariables: [
            { name: "resolved_count", type: "number" },
            { name: "resolution_log", type: "string" },
            { name: "resolution_summary", type: "string" },
            { name: "remaining_manual", type: "number" },
            { name: "needs_manual_update", type: "string" },
          ],
        },
      }),

      // 8. Discrepancy report Sheets
      sheetsNode(
        "8",
        "Log Reconciliation Report to Sheets",
        1720,
        "DataReconciliation",
        '[["{reconciliation_date}","{matched_count}","{total_discrepancies}","{auto_resolvable_count}","{resolved_count}","{manual_review_count}","{missing_in_crm}","{missing_in_billing}","{missing_in_support}","{data_health_score}","{resolution_summary}"]]',
      ),

      // 9. Resolution executor HubSpot
      hubspotPostNode(
        "9",
        "Push Resolutions to HubSpot CRM",
        1960,
        "crm/v3/objects/contacts/batch/update",
        [{ key: "inputs", value: "{normalized_data}" }],
        [
          {
            name: "status",
            customName: "hubspot_update_status",
            type: "string",
          },
          {
            name: "results",
            customName: "hubspot_updated_records",
            type: "string",
          },
        ],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 1375. MULTI-STEP APPROVAL WORKFLOW (9 nodes)
  // ===================================================================

  "Multi Step Approval Workflow": {
    summary:
      "Receives a request submission via webhook, extracts request data with AI, builds the approval chain with runCode, sends a Step 1 manager approval email, waits 3 days for response, evaluates the decision with a condition, routes to Step 2 department head approval email, advances to Step 3 finance approval condition, and logs the full audit trail to Google Sheets.",
    nodes: [
      trigger("Webhook: Request submission received", "webhook_app", {
        source: "request_system",
        event: "request.submitted",
        howItStarts:
          "Fires when an employee submits a request requiring multi-level approval — budget requests, travel authorizations, project proposals, or policy exceptions",
        setup:
          "Configure webhook from your internal request portal. Set OpenAI API key for data extraction. Configure email for approval notifications at each level. Provide Google Sheets spreadsheet ID for audit logging.",
        inputFields: [],
      }),

      // 2. AI extract request data
      aiNode(
        "2",
        "AI Extract Request Details",
        280,
        "You are a request parser. Extract the request type, title, description, requester name, requester email, department, amount, justification, urgency, manager email, dept head email, and finance approver email from the raw webhook payload. Respond JSON: {request_type: string, request_title: string, description: string, requester_name: string, requester_email: string, department: string, amount: number, justification: string, urgency: string, manager_email: string, dept_head_email: string, finance_email: string, request_id: string}",
        "Raw webhook payload: {webhook_payload}. Extract all request details regardless of payload format.",
        [
          { field: "request_type", as: "request_type", type: "string" },
          { field: "request_title", as: "request_title", type: "string" },
          { field: "description", as: "request_description", type: "string" },
          { field: "requester_name", as: "requester_name", type: "string" },
          { field: "requester_email", as: "requester_email", type: "string" },
          { field: "department", as: "department", type: "string" },
          { field: "amount", as: "request_amount", type: "number" },
          { field: "justification", as: "justification", type: "string" },
          { field: "urgency", as: "urgency", type: "string" },
          { field: "manager_email", as: "manager_email", type: "string" },
          { field: "dept_head_email", as: "dept_head_email", type: "string" },
          { field: "finance_email", as: "finance_email", type: "string" },
          { field: "request_id", as: "request_id", type: "string" },
        ],
      ),

      // 3. Approval chain builder runCode
      n("3", "runCode", "Build Approval Chain and Requirements", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Determine approval levels required based on amount and type
const amount = parseFloat(vars.request_amount || "0");
const reqType = (vars.request_type || "general").toLowerCase();
let approvalLevels = 1;
let requiresFinance = "no";
let requiresDeptHead = "no";
if (amount > 5000 || reqType === "capital_expenditure") {
  approvalLevels = 3;
  requiresFinance = "yes";
  requiresDeptHead = "yes";
} else if (amount > 1000 || reqType === "travel" || reqType === "hiring") {
  approvalLevels = 2;
  requiresDeptHead = "yes";
}
const chainDescription = "Level 1: Manager (" + (vars.manager_email || "N/A") + ")" +
  (requiresDeptHead === "yes" ? " → Level 2: Dept Head (" + (vars.dept_head_email || "N/A") + ")" : "") +
  (requiresFinance === "yes" ? " → Level 3: Finance (" + (vars.finance_email || "N/A") + ")" : "");
return {
  approval_levels: approvalLevels,
  requires_finance: requiresFinance,
  requires_dept_head: requiresDeptHead,
  chain_description: chainDescription,
  current_level: 1,
  submitted_at: new Date().toISOString()
};`,
          outputVariables: [
            { name: "approval_levels", type: "number" },
            { name: "requires_finance", type: "string" },
            { name: "requires_dept_head", type: "string" },
            { name: "chain_description", type: "string" },
            { name: "current_level", type: "number" },
            { name: "submitted_at", type: "string" },
          ],
        },
      }),

      // 4. Step 1 manager approval email
      emailNode(
        "4",
        "Step 1: Manager Approval Request",
        760,
        "{manager_email}",
        "Approval Required [{request_id}]: {request_title} — ${request_amount}",
        "<h2>Approval Request — Level 1 (Manager)</h2><p><strong>Request ID:</strong> {request_id}</p><p><strong>Type:</strong> {request_type}</p><p><strong>From:</strong> {requester_name} ({department})</p><p><strong>Amount:</strong> ${request_amount}</p><p><strong>Description:</strong> {request_description}</p><p><strong>Justification:</strong> {justification}</p><p><strong>Urgency:</strong> {urgency}</p><hr><p><strong>Approval Chain:</strong> {chain_description}</p><p>Please reply APPROVE or REJECT with comments.</p>",
      ),

      // 5. Delay 3 days
      n("5", "delayAction", "Wait 3 Days for Manager Response", 1000, {
        delayAction: { time: 3, timeFormat: "days" },
      }),

      // 6. Step 1 decision condition
      n("6", "condition", "Manager Approved?", 1240, {
        conditionDetails: {
          conditions: [
            { variable: "requires_dept_head", operator: "eq", value: "yes" },
          ],
        },
      }),

      // 7. Step 2 dept head approval email (True path — needs dept head)
      emailNode(
        "7",
        "Step 2: Department Head Approval Request",
        1480,
        "{dept_head_email}",
        "Escalated Approval [{request_id}]: {request_title} — ${request_amount}",
        "<h2>Approval Request — Level 2 (Department Head)</h2><p><strong>Request ID:</strong> {request_id}</p><p><strong>Type:</strong> {request_type}</p><p><strong>From:</strong> {requester_name} ({department})</p><p><strong>Amount:</strong> ${request_amount}</p><p><strong>Description:</strong> {request_description}</p><p><strong>Justification:</strong> {justification}</p><p><strong>Manager Approval:</strong> Approved (Level 1 passed)</p><hr><p><strong>Approval Chain:</strong> {chain_description}</p><p>Please reply APPROVE or REJECT with comments.</p>",
      ),

      // 8. Step 3 finance approval condition
      n("8", "condition", "Requires Finance Approval?", 1720, {
        conditionDetails: {
          conditions: [
            { variable: "requires_finance", operator: "eq", value: "yes" },
          ],
        },
      }),

      // 9. Audit logger Sheets
      sheetsNode(
        "9",
        "Log Full Approval Audit Trail",
        1960,
        "ApprovalAudit",
        '[["{request_id}","{request_type}","{request_title}","{requester_name}","{department}","{request_amount}","{urgency}","{approval_levels}","{chain_description}","{submitted_at}","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("6", "9", "output_2"),
      e("7", "8"),
      e("8", "9", "output_1"),
      e("8", "9", "output_2"),
    ],
  },

  // ===================================================================
  // 1376. MULTI-TOOL TASK AGGREGATOR (8 nodes)
  // ===================================================================

  "Multi Tool Task Aggregator": {
    summary:
      "Runs daily to fetch tasks from Jira, Asana, and GitHub Issues via their respective APIs, extracts email action items with runCode, uses AI to deduplicate and consolidate tasks, writes the unified dashboard to Google Sheets, and sends a priority digest to Slack.",
    nodes: [
      trigger("Schedule: Daily task aggregation", "schedule", {
        schedule: "Every weekday at 8:00 AM",
        howItStarts:
          "Runs every morning to aggregate tasks, issues, and action items from Jira, Asana, GitHub, and email into a single unified view with deduplication and priority ranking",
        setup:
          "Set API tokens for Jira, Asana, and GitHub. Set OpenAI API key for AI deduplication. Provide Google Sheets spreadsheet ID for the unified dashboard. Set Slack webhook URL for priority digest.",
        inputFields: [],
      }),

      // 2. Jira tasks fetch API
      n("2", "hitApi", "Fetch Open Tasks from Jira", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://{jira_domain}.atlassian.net/rest/api/3/search?jql=assignee=currentUser()+AND+status!=Done&maxResults=50&fields=summary,priority,status,duedate,project",
          headers: [
            { key: "Authorization", value: "Basic {jira_auth_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "issues", customName: "jira_issues", type: "string" },
            { name: "total", customName: "jira_count", type: "number" },
          ],
        },
      }),

      // 3. Asana tasks fetch API
      n("3", "hitApi", "Fetch Open Tasks from Asana", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://app.asana.com/api/1.0/tasks?assignee=me&workspace={asana_workspace_id}&completed_since=now&opt_fields=name,due_on,notes,projects.name,custom_fields",
          headers: [
            { key: "Authorization", value: "Bearer {asana_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "data", customName: "asana_tasks", type: "string" },
          ],
        },
      }),

      // 4. GitHub issues fetch API
      n("4", "hitApi", "Fetch Assigned GitHub Issues", 760, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.github.com/issues?filter=assigned&state=open&per_page=50&sort=updated",
          headers: [
            { key: "Authorization", value: "Bearer {github_token}" },
            { key: "Accept", value: "application/vnd.github.v3+json" },
          ],
          body: [],
          variables: [
            { name: "$", customName: "github_issues", type: "string" },
          ],
        },
      }),

      // 5. Email action items fetch runCode
      n("5", "runCode", "Extract Action Items from Recent Tasks", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse and normalize tasks from all three sources
const jira = JSON.parse(vars.jira_issues || "[]");
const asana = JSON.parse(vars.asana_tasks || "[]");
const github = JSON.parse(vars.github_issues || "[]");
let allTasks = [];
for (const issue of jira) {
  allTasks.push({
    source: "Jira",
    title: (issue.fields && issue.fields.summary) || "Untitled",
    priority: (issue.fields && issue.fields.priority && issue.fields.priority.name) || "Medium",
    due: (issue.fields && issue.fields.duedate) || "No date",
    status: (issue.fields && issue.fields.status && issue.fields.status.name) || "Open",
    key: issue.key || ""
  });
}
for (const task of asana) {
  allTasks.push({
    source: "Asana",
    title: task.name || "Untitled",
    priority: "Medium",
    due: task.due_on || "No date",
    status: "Open",
    key: task.gid || ""
  });
}
for (const issue of github) {
  allTasks.push({
    source: "GitHub",
    title: issue.title || "Untitled",
    priority: (issue.labels && issue.labels.some(l => l.name === "priority:high")) ? "High" : "Medium",
    due: "No date",
    status: issue.state || "open",
    key: issue.number ? "#" + issue.number : ""
  });
}
return {
  all_tasks_json: JSON.stringify(allTasks),
  total_task_count: allTasks.length,
  jira_task_count: jira.length,
  asana_task_count: asana.length,
  github_task_count: github.length,
  aggregation_date: new Date().toISOString().split("T")[0]
};`,
          outputVariables: [
            { name: "all_tasks_json", type: "string" },
            { name: "total_task_count", type: "number" },
            { name: "jira_task_count", type: "number" },
            { name: "asana_task_count", type: "number" },
            { name: "github_task_count", type: "number" },
            { name: "aggregation_date", type: "string" },
          ],
        },
      }),

      // 6. AI dedup consolidation
      aiNode(
        "6",
        "AI Deduplicate and Prioritize Tasks",
        1240,
        "You are a task management expert. Given aggregated tasks from multiple tools (Jira, Asana, GitHub), identify duplicates (same work tracked in multiple tools), merge them, and produce a prioritized task list. Rank by: overdue first, then high priority, then due date proximity. Respond JSON: {deduplicated_count: number, duplicates_found: number, priority_list: string, overdue_tasks: string, due_today: string, top_5_priorities: string, blocked_items: string}",
        "All tasks ({total_task_count} total — Jira: {jira_task_count}, Asana: {asana_task_count}, GitHub: {github_task_count}): {all_tasks_json}. Deduplicate and prioritize.",
        [
          {
            field: "deduplicated_count",
            as: "deduplicated_count",
            type: "number",
          },
          { field: "duplicates_found", as: "duplicates_found", type: "number" },
          { field: "priority_list", as: "priority_list", type: "string" },
          { field: "overdue_tasks", as: "overdue_tasks", type: "string" },
          { field: "due_today", as: "due_today", type: "string" },
          { field: "top_5_priorities", as: "top_5_priorities", type: "string" },
          { field: "blocked_items", as: "blocked_items", type: "string" },
        ],
      ),

      // 7. Unified dashboard Sheets
      sheetsNode(
        "7",
        "Write Unified Task Dashboard to Sheets",
        1480,
        "TaskDashboard",
        '[["{aggregation_date}","{total_task_count}","{deduplicated_count}","{duplicates_found}","{jira_task_count}","{asana_task_count}","{github_task_count}","{overdue_tasks}","{due_today}"]]',
      ),

      // 8. Priority digest Slack
      slackNode(
        "8",
        "Send Priority Task Digest to Slack",
        1720,
        "Daily Task Digest ({aggregation_date}):\\n*Total Tasks:* {deduplicated_count} (after removing {duplicates_found} duplicates)\\n*Sources:* Jira: {jira_task_count} | Asana: {asana_task_count} | GitHub: {github_task_count}\\n\\n*Top 5 Priorities:*\\n{top_5_priorities}\\n\\n*Overdue:* {overdue_tasks}\\n*Due Today:* {due_today}\\n*Blocked:* {blocked_items}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1377. MULTI-CHANNEL ALERT SYSTEM (7 nodes)
  // ===================================================================

  "Multi-Channel Alert System": {
    summary:
      "Receives critical events via webhook, extracts event data with AI, classifies severity with runCode, routes to the appropriate channel based on severity condition, and dispatches alerts simultaneously to Slack, email, and SMS based on the severity level.",
    nodes: [
      trigger("Webhook: Critical event detected", "webhook_app", {
        source: "monitoring_system",
        event: "alert.triggered",
        howItStarts:
          "Fires when a critical event is detected by any monitoring system — infrastructure alerts, security incidents, system outages, SLA breaches, or application errors",
        setup:
          "Configure webhook from your monitoring tools (Datadog, PagerDuty, CloudWatch, etc.). Set OpenAI API key for event extraction. Set Slack webhook URL, email, and SMS credentials for multi-channel dispatch.",
        inputFields: [],
      }),

      // 2. AI extract event data
      aiNode(
        "2",
        "AI Extract Alert Event Details",
        280,
        "You are an incident parser. Extract the alert type, severity, affected system, error message, timestamp, source system, impacted users count, region, service name, and recommended action from the raw webhook payload. Classify severity as P1 (critical), P2 (high), P3 (medium), or P4 (low). Respond JSON: {alert_type: string, severity: string, affected_system: string, error_message: string, alert_timestamp: string, source_system: string, impacted_users: number, region: string, service_name: string, recommended_action: string, on_call_email: string, on_call_phone: string}",
        "Raw webhook payload: {webhook_payload}. Extract all alert details and classify severity regardless of payload format.",
        [
          { field: "alert_type", as: "alert_type", type: "string" },
          { field: "severity", as: "severity", type: "string" },
          { field: "affected_system", as: "affected_system", type: "string" },
          { field: "error_message", as: "error_message", type: "string" },
          { field: "alert_timestamp", as: "alert_timestamp", type: "string" },
          { field: "source_system", as: "source_system", type: "string" },
          { field: "impacted_users", as: "impacted_users", type: "number" },
          { field: "region", as: "region", type: "string" },
          { field: "service_name", as: "service_name", type: "string" },
          {
            field: "recommended_action",
            as: "recommended_action",
            type: "string",
          },
          { field: "on_call_email", as: "on_call_email", type: "string" },
          { field: "on_call_phone", as: "on_call_phone", type: "string" },
        ],
      ),

      // 3. Severity classifier runCode
      n("3", "runCode", "Classify Severity and Determine Channels", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Determine which channels to activate based on severity
const severity = (vars.severity || "P4").toUpperCase();
const impacted = parseInt(vars.impacted_users || "0", 10);
let needsSms = "no";
let needsEmail = "yes";
let needsSlack = "yes";
let urgencyLabel = "Low";
if (severity === "P1" || impacted > 1000) {
  needsSms = "yes";
  urgencyLabel = "CRITICAL";
} else if (severity === "P2" || impacted > 100) {
  urgencyLabel = "HIGH";
} else if (severity === "P3") {
  urgencyLabel = "MEDIUM";
} else {
  urgencyLabel = "LOW";
  needsEmail = "no";
}
const alertSummary = urgencyLabel + " — " + (vars.alert_type || "Alert") + " on " + (vars.service_name || "Unknown") + " (" + (vars.region || "N/A") + ") — " + impacted + " users impacted";
return {
  needs_sms: needsSms,
  needs_email: needsEmail,
  needs_slack: needsSlack,
  urgency_label: urgencyLabel,
  alert_summary: alertSummary,
  is_critical: severity === "P1" ? "yes" : "no"
};`,
          outputVariables: [
            { name: "needs_sms", type: "string" },
            { name: "needs_email", type: "string" },
            { name: "needs_slack", type: "string" },
            { name: "urgency_label", type: "string" },
            { name: "alert_summary", type: "string" },
            { name: "is_critical", type: "string" },
          ],
        },
      }),

      // 4. Channel selection router condition
      n("4", "condition", "Is Critical Severity (P1)?", 760, {
        conditionDetails: {
          conditions: [
            { variable: "is_critical", operator: "eq", value: "yes" },
          ],
        },
      }),

      // 5. Slack alert dispatcher (both paths)
      slackNode(
        "5",
        "Dispatch Alert to Slack",
        1000,
        "[{urgency_label}] {alert_summary}\\n*System:* {affected_system} ({service_name})\\n*Error:* {error_message}\\n*Region:* {region} | *Impacted Users:* {impacted_users}\\n*Source:* {source_system} at {alert_timestamp}\\n*Action:* {recommended_action}",
      ),

      // 6. Email alert dispatcher (True path — critical)
      emailNode(
        "6",
        "Email Critical Alert to On-Call",
        1240,
        "{on_call_email}",
        "[{severity}] {urgency_label}: {alert_type} — {service_name} ({region})",
        "<h2 style='color:red;'>{urgency_label} Alert</h2><p><strong>Type:</strong> {alert_type}</p><p><strong>System:</strong> {affected_system} — {service_name}</p><p><strong>Error:</strong> {error_message}</p><p><strong>Region:</strong> {region}</p><p><strong>Impacted Users:</strong> {impacted_users}</p><p><strong>Time:</strong> {alert_timestamp}</p><p><strong>Source:</strong> {source_system}</p><hr><p><strong>Recommended Action:</strong> {recommended_action}</p>",
        400,
      ),

      // 7. SMS alert dispatcher (True path — critical only)
      smsNode(
        "7",
        "SMS Alert to On-Call Engineer",
        1480,
        "{on_call_phone}",
        "[{severity}] {urgency_label}: {alert_type} on {service_name} ({region}). {impacted_users} users impacted. Action: {recommended_action}",
        400,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5", "output_2"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1378. NPS SURVEY AUTOMATION (8 nodes)
  // ===================================================================

  "NPS Survey Automation": {
    summary:
      "Triggers 30 days after signup on a schedule, fetches customer context from HubSpot, sends the NPS survey via email, waits 7 days for a response, checks if a response was received with a condition, classifies the score with a condition, sends a thank-you email to promoters, and alerts the customer success team on Slack for detractors.",
    nodes: [
      trigger("Schedule: 30-day post-signup NPS survey", "schedule", {
        schedule: "Every day at 10:00 AM",
        howItStarts:
          "Runs daily to identify customers who signed up exactly 30 days ago and sends them an NPS (Net Promoter Score) survey to measure satisfaction and loyalty",
        setup:
          "Set HubSpot token for customer data. Configure email for survey delivery and follow-ups. Set Slack webhook for detractor alerts. Ensure NPS survey link is configured in the email template.",
        inputFields: [],
      }),

      // 2. Customer context fetch HubSpot
      hubspotGetNode(
        "2",
        "Fetch 30-Day Customers from HubSpot",
        280,
        "crm/v3/objects/contacts/search",
        [
          { name: "results", customName: "eligible_customers", type: "string" },
          { name: "total", customName: "eligible_count", type: "number" },
        ],
      ),

      // 3. Survey delivery email
      emailNode(
        "3",
        "Send NPS Survey Email",
        520,
        "{eligible_customers}",
        "How likely are you to recommend us? Quick 1-minute survey",
        "<h2>We'd love your feedback!</h2><p>Hi there,</p><p>You've been with us for 30 days now, and we'd love to know how things are going.</p><p><strong>On a scale of 0-10, how likely are you to recommend us to a friend or colleague?</strong></p><p><a href='{survey_link}?customer_id={customer_id}'>Take the 1-minute survey</a></p><p>Your feedback directly shapes our product roadmap. Thank you for being a valued customer!</p>",
      ),

      // 4. Delay 7 days for response collection
      n("4", "delayAction", "Wait 7 Days for Survey Responses", 760, {
        delayAction: { time: 7, timeFormat: "days" },
      }),

      // 5. Response collection condition
      n("5", "condition", "Survey Response Received?", 1000, {
        conditionDetails: {
          conditions: [{ variable: "nps_score", operator: "gte", value: "0" }],
        },
      }),

      // 6. Score classification router condition (True path — response received)
      n("6", "condition", "NPS Score 9-10 (Promoter)?", 1240, {
        conditionDetails: {
          conditions: [{ variable: "nps_score", operator: "gte", value: "9" }],
        },
      }),

      // 7. Promoter path email (True path — score >= 9)
      emailNode(
        "7",
        "Email Thank You to Promoter",
        1480,
        "{customer_email}",
        "Thank you for your amazing feedback!",
        "<h2>You made our day!</h2><p>Thank you for your fantastic NPS score of {nps_score}/10! We're thrilled you're having a great experience.</p><p>Would you be willing to share your experience? We'd love to feature your story:</p><ul><li><a href='{review_link}'>Leave a public review</a></li><li><a href='{referral_link}'>Refer a friend and earn rewards</a></li></ul><p>Your support means the world to us!</p>",
      ),

      // 8. Detractor path Slack alert (False path — score < 9)
      slackNode(
        "8",
        "Alert CS Team: Detractor/Passive Response",
        1480,
        "NPS Alert — Low Score Received:\\n*Customer:* {customer_email}\\n*NPS Score:* {nps_score}/10\\n*Category:* {nps_score >= 7 ? 'Passive' : 'Detractor'}\\n*Feedback:* {nps_feedback}\\n\\nPlease reach out within 24 hours to address concerns and prevent churn.",
        400,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("6", "7", "output_1"),
      e("6", "8", "output_2"),
    ],
  },

  // ===================================================================
  // 1379. NEW ORDER NOTIFICATION (7 nodes)
  // ===================================================================

  "New Order Notification": {
    summary:
      "Receives an order event via webhook, extracts order data with AI, performs a quick fraud check with runCode, sends a customer confirmation email, updates the CRM record in HubSpot, notifies the team on Slack, and logs any errors to Google Sheets.",
    nodes: [
      trigger("Webhook: New order placed", "webhook_app", {
        source: "ecommerce_platform",
        event: "order.created",
        howItStarts:
          "Fires when a customer completes a new order on the ecommerce platform, including order details, customer info, payment status, and line items",
        setup:
          "Configure webhook from Shopify, WooCommerce, or your ecommerce platform. Set OpenAI API key. Configure email for order confirmations. Set HubSpot token for CRM updates. Set Slack webhook and Google Sheets ID.",
        inputFields: [],
      }),

      // 2. AI extract order data
      aiNode(
        "2",
        "AI Extract Order Details",
        280,
        "You are an ecommerce order parser. Extract the order ID, customer name, customer email, shipping address, line items with quantities and prices, subtotal, tax, shipping cost, total amount, payment method, payment status, and order notes from the raw webhook payload. Respond JSON: {order_id: string, customer_name: string, customer_email: string, shipping_address: string, line_items: string, subtotal: number, tax: number, shipping_cost: number, total_amount: number, payment_method: string, payment_status: string, order_notes: string, item_count: number}",
        "Raw webhook payload: {webhook_payload}. Extract all order details regardless of ecommerce platform format.",
        [
          { field: "order_id", as: "order_id", type: "string" },
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "shipping_address", as: "shipping_address", type: "string" },
          { field: "line_items", as: "line_items", type: "string" },
          { field: "subtotal", as: "subtotal", type: "number" },
          { field: "tax", as: "tax", type: "number" },
          { field: "shipping_cost", as: "shipping_cost", type: "number" },
          { field: "total_amount", as: "total_amount", type: "number" },
          { field: "payment_method", as: "payment_method", type: "string" },
          { field: "payment_status", as: "payment_status", type: "string" },
          { field: "order_notes", as: "order_notes", type: "string" },
          { field: "item_count", as: "item_count", type: "number" },
        ],
      ),

      // 3. Fraud quick check runCode
      n("3", "runCode", "Quick Fraud Risk Assessment", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Quick fraud heuristics check
const total = parseFloat(vars.total_amount || "0");
const email = (vars.customer_email || "").toLowerCase();
const payMethod = (vars.payment_method || "").toLowerCase();
const itemCount = parseInt(vars.item_count || "0", 10);
let riskScore = 0;
let riskFactors = [];
if (total > 500) { riskScore += 20; riskFactors.push("High value order ($" + total + ")"); }
if (total > 2000) { riskScore += 30; riskFactors.push("Very high value order"); }
if (email.includes("temp") || email.includes("disposable") || email.includes("guerrilla")) { riskScore += 25; riskFactors.push("Suspicious email provider"); }
if (itemCount > 10) { riskScore += 15; riskFactors.push("Large item count (" + itemCount + ")"); }
if (payMethod.includes("prepaid") || payMethod.includes("gift")) { riskScore += 20; riskFactors.push("Prepaid/gift card payment"); }
const fraudRisk = riskScore >= 50 ? "HIGH" : riskScore >= 25 ? "MEDIUM" : "LOW";
const passesCheck = riskScore < 50 ? "yes" : "no";
return {
  fraud_risk_score: riskScore,
  fraud_risk_level: fraudRisk,
  risk_factors: riskFactors.join("; ") || "none",
  passes_fraud_check: passesCheck,
  checked_at: new Date().toISOString()
};`,
          outputVariables: [
            { name: "fraud_risk_score", type: "number" },
            { name: "fraud_risk_level", type: "string" },
            { name: "risk_factors", type: "string" },
            { name: "passes_fraud_check", type: "string" },
            { name: "checked_at", type: "string" },
          ],
        },
      }),

      // 4. Customer confirmation email
      emailNode(
        "4",
        "Send Order Confirmation Email",
        760,
        "{customer_email}",
        "Order Confirmed #{order_id} — Thank you, {customer_name}!",
        "<h2>Order Confirmation</h2><p>Hi {customer_name},</p><p>Thank you for your order!</p><p><strong>Order #{order_id}</strong></p><p><strong>Items:</strong> {line_items}</p><p><strong>Subtotal:</strong> ${subtotal}</p><p><strong>Tax:</strong> ${tax}</p><p><strong>Shipping:</strong> ${shipping_cost}</p><p><strong>Total:</strong> ${total_amount}</p><p><strong>Payment:</strong> {payment_method} ({payment_status})</p><p><strong>Shipping to:</strong> {shipping_address}</p><p>We'll notify you when your order ships!</p>",
      ),

      // 5. CRM record update HubSpot
      hubspotPostNode(
        "5",
        "Update Customer Record in HubSpot",
        1000,
        "crm/v3/objects/deals",
        [
          {
            key: "properties",
            value:
              '{"dealname":"Order #{order_id}","amount":"{total_amount}","pipeline":"default","dealstage":"closedwon","description":"{line_items}"}',
          },
        ],
        [
          { name: "id", customName: "deal_id", type: "string" },
          {
            name: "properties.dealname",
            customName: "deal_name",
            type: "string",
          },
        ],
      ),

      // 6. Team Slack notification
      slackNode(
        "6",
        "Notify Team of New Order",
        1240,
        "New Order #{order_id}:\\n*Customer:* {customer_name} ({customer_email})\\n*Items:* {item_count} items — {line_items}\\n*Total:* ${total_amount} ({payment_method})\\n*Fraud Risk:* {fraud_risk_level} (Score: {fraud_risk_score})\\n*Shipping:* {shipping_address}",
      ),

      // 7. Error handler Sheets
      sheetsNode(
        "7",
        "Log Order to Tracking Sheet",
        1480,
        "OrderLog",
        '[["{checked_at}","{order_id}","{customer_name}","{customer_email}","{total_amount}","{item_count}","{payment_method}","{payment_status}","{fraud_risk_level}","{fraud_risk_score}","{risk_factors}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1380. NEW USER PROVISIONING (9 nodes)
  // ===================================================================

  "New User Provisioning": {
    summary:
      "Receives an HR trigger via webhook for new employee onboarding, extracts employee data with AI, sets up identity provider access via Okta API, provisions email through Google Admin API, configures application access with runCode, sends a Slack welcome message, creates equipment requests in Asana, notifies the manager via email, and verifies provisioning completion with a condition.",
    nodes: [
      trigger("Webhook: New employee onboarding triggered", "webhook_app", {
        source: "hr_system",
        event: "employee.onboarding_started",
        howItStarts:
          "Fires when HR initiates onboarding for a new employee, including personal details, role, department, start date, manager info, and required system access",
        setup:
          "Configure webhook from your HRIS (BambooHR, Workday, etc.). Set OpenAI API key. Provide Okta API credentials for identity setup. Set Google Admin credentials for email provisioning. Set Asana token for equipment requests. Configure Slack and email for notifications.",
        inputFields: [],
      }),

      // 2. AI extract employee data
      aiNode(
        "2",
        "AI Extract New Employee Details",
        280,
        "You are an HR data parser. Extract the new employee's full name, personal email, work email to create, role title, department, start date, manager name, manager email, office location, team name, required applications list, and equipment needs from the raw webhook payload. Respond JSON: {full_name: string, personal_email: string, work_email: string, role_title: string, department: string, start_date: string, manager_name: string, manager_email: string, office_location: string, team_name: string, required_apps: string, equipment_needs: string, employee_id: string}",
        "Raw webhook payload: {webhook_payload}. Extract all new employee onboarding details regardless of HRIS payload format.",
        [
          { field: "full_name", as: "full_name", type: "string" },
          { field: "personal_email", as: "personal_email", type: "string" },
          { field: "work_email", as: "work_email", type: "string" },
          { field: "role_title", as: "role_title", type: "string" },
          { field: "department", as: "department", type: "string" },
          { field: "start_date", as: "start_date", type: "string" },
          { field: "manager_name", as: "manager_name", type: "string" },
          { field: "manager_email", as: "manager_email", type: "string" },
          { field: "office_location", as: "office_location", type: "string" },
          { field: "team_name", as: "team_name", type: "string" },
          { field: "required_apps", as: "required_apps", type: "string" },
          { field: "equipment_needs", as: "equipment_needs", type: "string" },
          { field: "employee_id", as: "employee_id", type: "string" },
        ],
      ),

      // 3. Identity provider setup Okta API
      n("3", "hitApi", "Create Okta Identity Account", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://{okta_domain}.okta.com/api/v1/users?activate=true",
          headers: [
            { key: "Authorization", value: "SSWS {okta_api_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "profile",
              value:
                '{"firstName":"{full_name}","login":"{work_email}","email":"{work_email}","department":"{department}","title":"{role_title}"}',
            },
            { key: "groupIds", value: '["{department}_group_id"]' },
          ],
          variables: [
            { name: "id", customName: "okta_user_id", type: "string" },
            { name: "status", customName: "okta_status", type: "string" },
            { name: "profile.login", customName: "okta_login", type: "string" },
          ],
        },
      }),

      // 4. Email provisioning Google Admin API
      n(
        "4",
        "hitApi",
        "Provision Google Workspace Email",
        520,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://admin.googleapis.com/admin/directory/v1/users",
            headers: [
              { key: "Authorization", value: "Bearer {google_admin_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              { key: "primaryEmail", value: "{work_email}" },
              {
                key: "name",
                value: '{"givenName":"{full_name}","familyName":""}',
              },
              { key: "password", value: "{generated_temp_password}" },
              { key: "changePasswordAtNextLogin", value: "true" },
              { key: "orgUnitPath", value: "/{department}" },
            ],
            variables: [
              { name: "id", customName: "google_user_id", type: "string" },
              {
                name: "primaryEmail",
                customName: "provisioned_email",
                type: "string",
              },
              {
                name: "isMailboxSetup",
                customName: "mailbox_ready",
                type: "boolean",
              },
            ],
          },
        },
        400,
      ),

      // 5. Application access provisioning runCode
      n("5", "runCode", "Configure Application Access Permissions", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Determine application access based on department and role
const dept = (vars.department || "").toLowerCase();
const role = (vars.role_title || "").toLowerCase();
const requestedApps = (vars.required_apps || "").split(",").map(a => a.trim()).filter(Boolean);
const baseApps = ["Slack", "Google Workspace", "1Password"];
const deptApps = {
  engineering: ["GitHub", "Jira", "AWS Console", "Datadog"],
  sales: ["Salesforce", "Outreach", "Gong", "LinkedIn Sales Navigator"],
  marketing: ["HubSpot", "Figma", "Google Analytics", "Mailchimp"],
  product: ["Jira", "Figma", "Amplitude", "Notion"],
  finance: ["QuickBooks", "Expensify", "Stripe Dashboard"],
  hr: ["BambooHR", "Greenhouse", "Lattice"]
};
const roleApps = role.includes("manager") || role.includes("lead") ? ["Lattice", "15Five"] : [];
const allApps = [...new Set([...baseApps, ...(deptApps[dept] || []), ...roleApps, ...requestedApps])];
return {
  provisioned_apps: allApps.join(", "),
  app_count: allApps.length,
  base_apps: baseApps.join(", "),
  dept_specific_apps: (deptApps[dept] || []).join(", ") || "none",
  provisioning_status: "completed",
  provisioned_at: new Date().toISOString()
};`,
          outputVariables: [
            { name: "provisioned_apps", type: "string" },
            { name: "app_count", type: "number" },
            { name: "base_apps", type: "string" },
            { name: "dept_specific_apps", type: "string" },
            { name: "provisioning_status", type: "string" },
            { name: "provisioned_at", type: "string" },
          ],
        },
      }),

      // 6. Slack welcome message
      slackNode(
        "6",
        "Send Welcome Message to Slack",
        1000,
        "Welcome {full_name} to the team!\\n*Role:* {role_title} ({department})\\n*Manager:* {manager_name}\\n*Start Date:* {start_date}\\n*Office:* {office_location}\\n*Team:* {team_name}\\n\\nProvisioned {app_count} applications: {provisioned_apps}\\nOkta: {okta_status} | Email: {provisioned_email}",
      ),

      // 7. Equipment request Asana API
      n("7", "hitApi", "Create Equipment Request in Asana", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://app.asana.com/api/1.0/tasks",
          headers: [
            { key: "Authorization", value: "Bearer {asana_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "data",
              value:
                '{"name":"Equipment for {full_name} ({role_title})","notes":"New hire starting {start_date}.\\nDepartment: {department}\\nLocation: {office_location}\\n\\nEquipment needed:\\n{equipment_needs}\\n\\nPlease have ready by {start_date}.","projects":["{equipment_project_id}"],"due_on":"{start_date}"}',
            },
          ],
          variables: [
            {
              name: "data.gid",
              customName: "equipment_task_id",
              type: "string",
            },
            {
              name: "data.name",
              customName: "equipment_task_name",
              type: "string",
            },
          ],
        },
      }),

      // 8. Manager notification email
      emailNode(
        "8",
        "Notify Manager of Provisioning Complete",
        1480,
        "{manager_email}",
        "Onboarding Complete: {full_name} — {role_title} starting {start_date}",
        "<h2>New Hire Provisioning Complete</h2><p>Hi {manager_name},</p><p><strong>{full_name}</strong> has been fully provisioned for their start date of <strong>{start_date}</strong>.</p><h3>Provisioning Summary</h3><ul><li><strong>Okta SSO:</strong> {okta_status} ({okta_login})</li><li><strong>Email:</strong> {provisioned_email}</li><li><strong>Applications ({app_count}):</strong> {provisioned_apps}</li><li><strong>Equipment:</strong> Request created (Asana task #{equipment_task_id})</li></ul><p><strong>Location:</strong> {office_location}</p><p>Please ensure you have a first-day plan ready for {full_name}.</p>",
      ),

      // 9. Provisioning verification condition
      n("9", "condition", "All Systems Provisioned Successfully?", 1720, {
        conditionDetails: {
          conditions: [
            {
              variable: "provisioning_status",
              operator: "eq",
              value: "completed",
            },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 1381. OFFER LETTER APPROVAL WORKFLOW (9 nodes)
  // ===================================================================

  "Offer Letter Approval Workflow": {
    summary:
      "Receives an offer submission via webhook, extracts offer data with AI, validates compensation against bands with runCode, calculates budget impact with runCode, routes based on approval requirements with a condition, sends an approver notification email, handles modifications with a condition, generates the offer letter with runCode, and sends a completion notification email.",
    nodes: [
      trigger("Webhook: Offer letter submitted for approval", "webhook_app", {
        source: "recruiting_system",
        event: "offer.submitted",
        howItStarts:
          "Fires when a recruiter submits an offer letter for approval, including candidate details, compensation package, role information, and approver chain",
        setup:
          "Configure webhook from your ATS (Greenhouse, Lever, etc.). Set OpenAI API key. Configure email for approval notifications. Ensure compensation band data is accessible.",
        inputFields: [],
      }),

      // 2. AI extract offer data
      aiNode(
        "2",
        "AI Extract Offer Letter Details",
        280,
        "You are an HR offer parser. Extract the candidate name, candidate email, role title, department, level/band, base salary, bonus percentage, equity shares, sign-on bonus, start date, recruiter name, recruiter email, hiring manager email, and VP email from the raw webhook payload. Respond JSON: {candidate_name: string, candidate_email: string, role_title: string, department: string, level: string, base_salary: number, bonus_pct: number, equity_shares: number, sign_on_bonus: number, start_date: string, recruiter_name: string, recruiter_email: string, hiring_manager_email: string, vp_email: string, offer_id: string}",
        "Raw webhook payload: {webhook_payload}. Extract all offer details regardless of ATS payload format.",
        [
          { field: "candidate_name", as: "candidate_name", type: "string" },
          { field: "candidate_email", as: "candidate_email", type: "string" },
          { field: "role_title", as: "role_title", type: "string" },
          { field: "department", as: "department", type: "string" },
          { field: "level", as: "level", type: "string" },
          { field: "base_salary", as: "base_salary", type: "number" },
          { field: "bonus_pct", as: "bonus_pct", type: "number" },
          { field: "equity_shares", as: "equity_shares", type: "number" },
          { field: "sign_on_bonus", as: "sign_on_bonus", type: "number" },
          { field: "start_date", as: "start_date", type: "string" },
          { field: "recruiter_name", as: "recruiter_name", type: "string" },
          { field: "recruiter_email", as: "recruiter_email", type: "string" },
          {
            field: "hiring_manager_email",
            as: "hiring_manager_email",
            type: "string",
          },
          { field: "vp_email", as: "vp_email", type: "string" },
          { field: "offer_id", as: "offer_id", type: "string" },
        ],
      ),

      // 3. Compensation band validator runCode
      n("3", "runCode", "Validate Against Compensation Bands", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Validate salary against comp bands for role level
const salary = parseFloat(vars.base_salary || "0");
const level = (vars.level || "").toLowerCase();
const bands = {
  "ic1": { min: 60000, mid: 80000, max: 100000 },
  "ic2": { min: 80000, mid: 110000, max: 140000 },
  "ic3": { min: 110000, mid: 145000, max: 180000 },
  "ic4": { min: 140000, mid: 180000, max: 220000 },
  "ic5": { min: 170000, mid: 220000, max: 270000 },
  "m1": { min: 120000, mid: 155000, max: 190000 },
  "m2": { min: 150000, mid: 195000, max: 240000 },
  "director": { min: 180000, mid: 240000, max: 300000 },
  "vp": { min: 220000, mid: 300000, max: 400000 }
};
const band = bands[level] || { min: 50000, mid: 100000, max: 150000 };
const withinBand = salary >= band.min && salary <= band.max ? "yes" : "no";
const percentile = Math.round(((salary - band.min) / (band.max - band.min)) * 100);
const aboveMid = salary > band.mid ? "yes" : "no";
const needsException = withinBand === "no" ? "yes" : (aboveMid === "yes" ? "review" : "no");
return {
  band_min: band.min,
  band_mid: band.mid,
  band_max: band.max,
  within_band: withinBand,
  band_percentile: percentile,
  above_midpoint: aboveMid,
  needs_exception: needsException,
  band_label: level.toUpperCase() + " ($" + band.min.toLocaleString() + " - $" + band.max.toLocaleString() + ")"
};`,
          outputVariables: [
            { name: "band_min", type: "number" },
            { name: "band_mid", type: "number" },
            { name: "band_max", type: "number" },
            { name: "within_band", type: "string" },
            { name: "band_percentile", type: "number" },
            { name: "above_midpoint", type: "string" },
            { name: "needs_exception", type: "string" },
            { name: "band_label", type: "string" },
          ],
        },
      }),

      // 4. Budget impact calculator runCode
      n("4", "runCode", "Calculate Total Compensation and Budget Impact", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Calculate total comp package and annual budget impact
const base = parseFloat(vars.base_salary || "0");
const bonusPct = parseFloat(vars.bonus_pct || "0");
const equity = parseInt(vars.equity_shares || "0", 10);
const signOn = parseFloat(vars.sign_on_bonus || "0");
const annualBonus = base * (bonusPct / 100);
const equityValue = equity * 25; // estimated per-share value
const totalComp = base + annualBonus + equityValue;
const firstYearCost = totalComp + signOn;
const loadedCost = firstYearCost * 1.3; // benefits, taxes, overhead
const approvalLevel = loadedCost > 300000 ? "VP" : loadedCost > 200000 ? "Director" : "Manager";
const requiresVpApproval = approvalLevel === "VP" ? "yes" : "no";
return {
  annual_bonus: annualBonus,
  equity_value: equityValue,
  total_comp: totalComp,
  first_year_cost: firstYearCost,
  loaded_cost: Math.round(loadedCost),
  approval_level: approvalLevel,
  requires_vp_approval: requiresVpApproval,
  comp_summary: "Base: $" + base.toLocaleString() + " + Bonus: $" + annualBonus.toLocaleString() + " + Equity: $" + equityValue.toLocaleString() + " = Total: $" + totalComp.toLocaleString()
};`,
          outputVariables: [
            { name: "annual_bonus", type: "number" },
            { name: "equity_value", type: "number" },
            { name: "total_comp", type: "number" },
            { name: "first_year_cost", type: "number" },
            { name: "loaded_cost", type: "number" },
            { name: "approval_level", type: "string" },
            { name: "requires_vp_approval", type: "string" },
            { name: "comp_summary", type: "string" },
          ],
        },
      }),

      // 5. Routing engine condition
      n("5", "condition", "Requires VP-Level Approval?", 1000, {
        conditionDetails: {
          conditions: [
            { variable: "requires_vp_approval", operator: "eq", value: "yes" },
          ],
        },
      }),

      // 6. Approver notification email (True path — VP required)
      emailNode(
        "6",
        "Send Approval Request to VP",
        1240,
        "{vp_email}",
        "Offer Approval Required [{offer_id}]: {candidate_name} — {role_title}",
        "<h2>Offer Letter Approval Required</h2><p><strong>Offer ID:</strong> {offer_id}</p><p><strong>Candidate:</strong> {candidate_name}</p><p><strong>Role:</strong> {role_title} ({department}, {level})</p><hr><h3>Compensation Package</h3><p>{comp_summary}</p><p><strong>Sign-on Bonus:</strong> ${sign_on_bonus}</p><p><strong>First Year Loaded Cost:</strong> ${loaded_cost}</p><h3>Band Analysis</h3><p><strong>Band:</strong> {band_label}</p><p><strong>Within Band:</strong> {within_band} | Percentile: {band_percentile}%</p><p><strong>Exception Required:</strong> {needs_exception}</p><p><strong>Start Date:</strong> {start_date}</p><p><strong>Recruiter:</strong> {recruiter_name}</p><p>Please reply APPROVE, MODIFY, or REJECT.</p>",
      ),

      // 7. Modification handler condition
      n("7", "condition", "Offer Within Compensation Band?", 1480, {
        conditionDetails: {
          conditions: [
            { variable: "within_band", operator: "eq", value: "yes" },
          ],
        },
      }),

      // 8. Offer letter generation runCode (True path — within band)
      n("8", "runCode", "Generate Offer Letter Document", 1720, {
        runCodeDetails: {
          language: "javascript",
          code: `// Generate the offer letter HTML
const letter = "<div style='font-family:Arial,sans-serif;max-width:700px;margin:auto;'>" +
  "<h1>Offer of Employment</h1>" +
  "<p>Dear " + (vars.candidate_name || "Candidate") + ",</p>" +
  "<p>We are pleased to extend an offer for the position of <strong>" + (vars.role_title || "N/A") + "</strong> in our " + (vars.department || "N/A") + " department.</p>" +
  "<h3>Compensation</h3>" +
  "<ul>" +
  "<li>Base Salary: $" + (vars.base_salary || "0") + " per year</li>" +
  "<li>Annual Bonus Target: " + (vars.bonus_pct || "0") + "% of base salary</li>" +
  "<li>Equity: " + (vars.equity_shares || "0") + " shares (4-year vest, 1-year cliff)</li>" +
  (parseFloat(vars.sign_on_bonus || "0") > 0 ? "<li>Sign-on Bonus: $" + (vars.sign_on_bonus || "0") + "</li>" : "") +
  "</ul>" +
  "<p><strong>Start Date:</strong> " + (vars.start_date || "TBD") + "</p>" +
  "<p>This offer is contingent upon successful completion of a background check.</p>" +
  "<p>Please sign and return within 5 business days.</p>" +
  "<p>We are excited to welcome you to the team!</p>" +
  "</div>";
return { offer_letter_html: letter, letter_generated: "yes", generated_at: new Date().toISOString() };`,
          outputVariables: [
            { name: "offer_letter_html", type: "string" },
            { name: "letter_generated", type: "string" },
            { name: "generated_at", type: "string" },
          ],
        },
      }),

      // 9. Completion notifier email
      emailNode(
        "9",
        "Notify Recruiter of Offer Letter Ready",
        1960,
        "{recruiter_email}",
        "Offer Letter Ready [{offer_id}]: {candidate_name} — {role_title}",
        "<h2>Offer Letter Generated and Approved</h2><p><strong>Offer ID:</strong> {offer_id}</p><p><strong>Candidate:</strong> {candidate_name} ({candidate_email})</p><p><strong>Role:</strong> {role_title} ({department})</p><p><strong>Comp:</strong> {comp_summary}</p><p><strong>Band:</strong> {band_label} — Percentile: {band_percentile}%</p><p><strong>Approval Level:</strong> {approval_level}</p><p><strong>Start Date:</strong> {start_date}</p><p>The offer letter is ready for delivery. Please review and send to the candidate.</p>",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("6", "7"),
      e("7", "8", "output_1"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 1382. OFFICE SUPPLY AUTO-REORDER (7 nodes)
  // ===================================================================

  "Office Supply Auto-Reorder": {
    summary:
      "Runs weekly to assess inventory levels with AI, generates a reorder list with runCode, composes supplier order emails with AI, sends the order to the supplier via email, notifies the office team on Slack, and tracks the budget in Google Sheets.",
    nodes: [
      trigger("Schedule: Weekly office supply check", "schedule", {
        schedule: "Every Monday at 9:00 AM",
        howItStarts:
          "Runs every Monday to review current office supply inventory levels, identify items below reorder thresholds, and automatically generate purchase orders for depleted supplies",
        setup:
          "Set OpenAI API key for AI inventory assessment and supplier email composition. Configure email for supplier orders. Set Slack webhook for team notifications. Provide Google Sheets spreadsheet ID for budget tracking.",
        inputFields: [],
      }),

      // 2. AI inventory assessment
      aiNode(
        "2",
        "AI Assess Current Inventory Levels",
        280,
        "You are an office supply inventory manager. Review the current inventory data and identify items that are below their reorder point. For each low item, specify the item name, current quantity, reorder point, suggested order quantity, unit cost, and priority. Respond JSON: {low_items_count: number, low_items: string, total_reorder_cost: number, urgent_items: string, routine_items: string, inventory_health: string, check_date: string}",
        "Current inventory data from tracking sheet. Check all categories: paper goods, toner/ink, writing instruments, cleaning supplies, kitchen supplies, tech accessories. Identify anything below reorder point.",
        [
          { field: "low_items_count", as: "low_items_count", type: "number" },
          { field: "low_items", as: "low_items", type: "string" },
          {
            field: "total_reorder_cost",
            as: "total_reorder_cost",
            type: "number",
          },
          { field: "urgent_items", as: "urgent_items", type: "string" },
          { field: "routine_items", as: "routine_items", type: "string" },
          { field: "inventory_health", as: "inventory_health", type: "string" },
          { field: "check_date", as: "check_date", type: "string" },
        ],
      ),

      // 3. Reorder list generator runCode
      n("3", "runCode", "Generate Reorder List and PO Details", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Build structured reorder list from AI assessment
const itemCount = parseInt(vars.low_items_count || "0", 10);
const totalCost = parseFloat(vars.total_reorder_cost || "0");
const urgentItems = vars.urgent_items || "none";
const routineItems = vars.routine_items || "none";
const poNumber = "PO-SUPPLY-" + new Date().toISOString().split("T")[0].replace(/-/g, "") + "-" + Math.floor(Math.random() * 1000);
const needsApproval = totalCost > 500 ? "yes" : "no";
const deliveryDate = new Date();
deliveryDate.setDate(deliveryDate.getDate() + (urgentItems !== "none" ? 3 : 7));
const orderSummary = "Items: " + itemCount + " | Total: $" + totalCost.toFixed(2) + " | Urgent: " + urgentItems + " | Routine: " + routineItems;
return {
  po_number: poNumber,
  order_summary: orderSummary,
  needs_approval: needsApproval,
  expected_delivery: deliveryDate.toISOString().split("T")[0],
  has_items_to_order: itemCount > 0 ? "yes" : "no",
  order_date: new Date().toISOString().split("T")[0]
};`,
          outputVariables: [
            { name: "po_number", type: "string" },
            { name: "order_summary", type: "string" },
            { name: "needs_approval", type: "string" },
            { name: "expected_delivery", type: "string" },
            { name: "has_items_to_order", type: "string" },
            { name: "order_date", type: "string" },
          ],
        },
      }),

      // 4. Supplier email composer AI
      aiNode(
        "4",
        "AI Compose Supplier Order Email",
        760,
        "You are a procurement assistant. Compose a professional supplier order email with the PO number, itemized list with quantities and unit costs, total amount, requested delivery date, and shipping address. Be polite but specific. Respond JSON: {email_subject: string, email_body: string, supplier_name: string}",
        "PO Number: {po_number}. Items to order: {low_items}. Urgent items: {urgent_items}. Routine items: {routine_items}. Total cost: ${total_reorder_cost}. Delivery by: {expected_delivery}. Compose the supplier order email.",
        [
          {
            field: "email_subject",
            as: "supplier_email_subject",
            type: "string",
          },
          { field: "email_body", as: "supplier_email_body", type: "string" },
          { field: "supplier_name", as: "supplier_name", type: "string" },
        ],
      ),

      // 5. Supplier order email
      emailNode(
        "5",
        "Send Order to Supplier",
        1000,
        "{supplier_email}",
        "{supplier_email_subject}",
        "{supplier_email_body}",
      ),

      // 6. Slack office notification
      slackNode(
        "6",
        "Notify Office Team of Supply Order",
        1240,
        "Office Supply Order Placed:\\n*PO:* {po_number}\\n*Items:* {low_items_count} items below reorder point\\n*Total:* ${total_reorder_cost}\\n*Urgent:* {urgent_items}\\n*Expected Delivery:* {expected_delivery}\\n*Inventory Health:* {inventory_health}",
      ),

      // 7. Budget tracker Sheets
      sheetsNode(
        "7",
        "Log Supply Order to Budget Tracker",
        1480,
        "SupplyBudget",
        '[["{order_date}","{po_number}","{low_items_count}","{total_reorder_cost}","{urgent_items}","{routine_items}","{expected_delivery}","{inventory_health}","{supplier_name}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1383. ORDER FRAUD DETECTOR (8 nodes)
  // ===================================================================

  "Order Fraud Detector": {
    summary:
      "Receives new orders via webhook, extracts order data with AI, fetches historical customer patterns from HubSpot, performs deep AI fraud analysis, calculates a composite risk score with runCode, routes based on risk level with a condition, sends high-risk alerts to Slack, and logs all decisions to Google Sheets.",
    nodes: [
      trigger("Webhook: New order submitted", "webhook_app", {
        source: "ecommerce_platform",
        event: "order.submitted",
        howItStarts:
          "Fires when a new order is submitted, before payment processing, including customer details, order items, shipping info, IP address, and device fingerprint for fraud analysis",
        setup:
          "Configure webhook from your ecommerce platform to fire before payment capture. Set OpenAI API key for AI fraud analysis. Set HubSpot token for historical data. Set Slack webhook for alerts. Provide Google Sheets spreadsheet ID.",
        inputFields: [],
      }),

      // 2. AI extract order data
      aiNode(
        "2",
        "AI Extract Order and Customer Data",
        280,
        "You are a fraud detection data parser. Extract all relevant order and customer signals from the webhook payload: customer email, name, IP address, device fingerprint, billing address, shipping address, order total, item categories, payment method, account age, previous order count, and any velocity indicators. Respond JSON: {customer_email: string, customer_name: string, ip_address: string, device_fingerprint: string, billing_address: string, shipping_address: string, order_total: number, item_categories: string, payment_method: string, account_age_days: number, previous_orders: number, order_id: string, currency: string}",
        "Raw webhook payload: {webhook_payload}. Extract every signal useful for fraud detection regardless of payload format.",
        [
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "ip_address", as: "ip_address", type: "string" },
          {
            field: "device_fingerprint",
            as: "device_fingerprint",
            type: "string",
          },
          { field: "billing_address", as: "billing_address", type: "string" },
          { field: "shipping_address", as: "shipping_address", type: "string" },
          { field: "order_total", as: "order_total", type: "number" },
          { field: "item_categories", as: "item_categories", type: "string" },
          { field: "payment_method", as: "payment_method", type: "string" },
          { field: "account_age_days", as: "account_age_days", type: "number" },
          { field: "previous_orders", as: "previous_orders", type: "number" },
          { field: "order_id", as: "order_id", type: "string" },
          { field: "currency", as: "currency", type: "string" },
        ],
      ),

      // 3. Historical pattern fetch HubSpot
      hubspotGetNode(
        "3",
        "Fetch Customer History from HubSpot",
        520,
        "crm/v3/objects/contacts/{customer_email}?properties=total_revenue,num_associated_deals,hs_lead_status,createdate,ip_city,ip_country",
        [
          {
            name: "properties.total_revenue",
            customName: "historical_revenue",
            type: "string",
          },
          {
            name: "properties.num_associated_deals",
            customName: "historical_orders",
            type: "string",
          },
          {
            name: "properties.hs_lead_status",
            customName: "lead_status",
            type: "string",
          },
          {
            name: "properties.ip_city",
            customName: "known_city",
            type: "string",
          },
          {
            name: "properties.ip_country",
            customName: "known_country",
            type: "string",
          },
        ],
      ),

      // 4. AI fraud analysis
      aiNode(
        "4",
        "AI Deep Fraud Analysis",
        760,
        "You are a fraud detection AI analyst. Analyze the order data against historical customer patterns and identify fraud indicators. Check for: address mismatches (billing vs shipping), unusual order amounts vs history, new account high-value orders, velocity anomalies, device/IP inconsistencies, high-risk item categories, and pattern deviations. Rate each signal 0-100 and provide an overall fraud probability. Respond JSON: {fraud_probability: number, address_mismatch_score: number, amount_anomaly_score: number, velocity_score: number, device_risk_score: number, category_risk_score: number, top_3_risks: string, recommendation: string, confidence: number, analysis_notes: string}",
        "Order: #{order_id}, ${order_total} {currency}. Customer: {customer_name} ({customer_email}), account age {account_age_days} days, {previous_orders} prior orders. IP: {ip_address}. Device: {device_fingerprint}. Billing: {billing_address}. Shipping: {shipping_address}. Items: {item_categories}. Payment: {payment_method}. Historical: revenue={historical_revenue}, orders={historical_orders}, status={lead_status}, known city={known_city}, known country={known_country}. Perform deep fraud analysis.",
        [
          {
            field: "fraud_probability",
            as: "fraud_probability",
            type: "number",
          },
          {
            field: "address_mismatch_score",
            as: "address_mismatch_score",
            type: "number",
          },
          {
            field: "amount_anomaly_score",
            as: "amount_anomaly_score",
            type: "number",
          },
          { field: "velocity_score", as: "velocity_score", type: "number" },
          {
            field: "device_risk_score",
            as: "device_risk_score",
            type: "number",
          },
          {
            field: "category_risk_score",
            as: "category_risk_score",
            type: "number",
          },
          { field: "top_3_risks", as: "top_3_risks", type: "string" },
          {
            field: "recommendation",
            as: "fraud_recommendation",
            type: "string",
          },
          { field: "confidence", as: "analysis_confidence", type: "number" },
          { field: "analysis_notes", as: "analysis_notes", type: "string" },
        ],
      ),

      // 5. Risk score calculator runCode
      n("5", "runCode", "Calculate Composite Fraud Risk Score", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Weighted composite fraud risk score
const fraudProb = parseFloat(vars.fraud_probability || "0");
const addrScore = parseFloat(vars.address_mismatch_score || "0");
const amtScore = parseFloat(vars.amount_anomaly_score || "0");
const velScore = parseFloat(vars.velocity_score || "0");
const devScore = parseFloat(vars.device_risk_score || "0");
const catScore = parseFloat(vars.category_risk_score || "0");
const composite = Math.round(
  (fraudProb * 0.30) +
  (addrScore * 0.20) +
  (amtScore * 0.15) +
  (velScore * 0.15) +
  (devScore * 0.10) +
  (catScore * 0.10)
);
const riskLevel = composite >= 70 ? "HIGH" : composite >= 40 ? "MEDIUM" : "LOW";
const action = composite >= 70 ? "BLOCK" : composite >= 40 ? "REVIEW" : "APPROVE";
const isHighRisk = composite >= 70 ? "yes" : "no";
return {
  composite_score: composite,
  risk_level: riskLevel,
  recommended_action: action,
  is_high_risk: isHighRisk,
  score_breakdown: "Fraud: " + fraudProb + " | Addr: " + addrScore + " | Amt: " + amtScore + " | Vel: " + velScore + " | Dev: " + devScore + " | Cat: " + catScore,
  scored_at: new Date().toISOString()
};`,
          outputVariables: [
            { name: "composite_score", type: "number" },
            { name: "risk_level", type: "string" },
            { name: "recommended_action", type: "string" },
            { name: "is_high_risk", type: "string" },
            { name: "score_breakdown", type: "string" },
            { name: "scored_at", type: "string" },
          ],
        },
      }),

      // 6. Risk routing condition
      n("6", "condition", "Is Order High Risk?", 1240, {
        conditionDetails: {
          conditions: [
            { variable: "is_high_risk", operator: "eq", value: "yes" },
          ],
        },
      }),

      // 7. High risk alert Slack (True path)
      slackNode(
        "7",
        "Alert Fraud Team: High Risk Order",
        1480,
        "FRAUD ALERT — Order #{order_id}\\n*Risk Level:* {risk_level} (Score: {composite_score}/100)\\n*Action:* {recommended_action}\\n*Customer:* {customer_name} ({customer_email})\\n*Amount:* ${order_total} {currency}\\n*Top Risks:* {top_3_risks}\\n*Score Breakdown:* {score_breakdown}\\n*Recommendation:* {fraud_recommendation}\\n*Notes:* {analysis_notes}",
      ),

      // 8. Decision logger Sheets (both paths)
      sheetsNode(
        "8",
        "Log Fraud Analysis Decision",
        1720,
        "FraudLog",
        '[["{scored_at}","{order_id}","{customer_email}","{order_total}","{composite_score}","{risk_level}","{recommended_action}","{top_3_risks}","{score_breakdown}","{fraud_recommendation}","{analysis_confidence}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("6", "8", "output_2"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1384. OUTBOUND SEQUENCE PAUSE TRIGGER (7 nodes)
  // ===================================================================

  "Outbound Sequence Pause Trigger": {
    summary:
      "Receives engagement events via webhook (email replies, website visits, form fills), extracts event data with AI, identifies the contact in HubSpot, uses AI to classify the intent, decides whether to pause the sequence with a condition, executes the pause via API, and notifies the sales rep on Slack.",
    nodes: [
      trigger("Webhook: Prospect engagement event", "webhook_app", {
        source: "engagement_tracker",
        event: "prospect.engaged",
        howItStarts:
          "Fires when a prospect in an outbound sales sequence shows engagement signals — email reply, link click, website visit, form submission, or social interaction that might indicate interest",
        setup:
          "Configure webhook from your sales engagement platform (Outreach, SalesLoft, etc.). Set OpenAI API key for intent classification. Set HubSpot token for contact identification. Configure Slack webhook for sales rep notifications.",
        inputFields: [],
      }),

      // 2. AI extract event data
      aiNode(
        "2",
        "AI Extract Engagement Event Data",
        280,
        "You are a sales engagement parser. Extract the engagement type (reply, click, visit, form_fill, social), contact email, contact name, sequence name, sequence step, engagement content (reply text, page visited, form data), timestamp, and sales rep email from the raw webhook payload. Respond JSON: {engagement_type: string, contact_email: string, contact_name: string, sequence_name: string, sequence_step: number, engagement_content: string, engagement_timestamp: string, rep_email: string, rep_name: string, sequence_id: string}",
        "Raw webhook payload: {webhook_payload}. Extract all engagement event details regardless of platform format.",
        [
          { field: "engagement_type", as: "engagement_type", type: "string" },
          { field: "contact_email", as: "contact_email", type: "string" },
          { field: "contact_name", as: "contact_name", type: "string" },
          { field: "sequence_name", as: "sequence_name", type: "string" },
          { field: "sequence_step", as: "sequence_step", type: "number" },
          {
            field: "engagement_content",
            as: "engagement_content",
            type: "string",
          },
          {
            field: "engagement_timestamp",
            as: "engagement_timestamp",
            type: "string",
          },
          { field: "rep_email", as: "rep_email", type: "string" },
          { field: "rep_name", as: "rep_name", type: "string" },
          { field: "sequence_id", as: "sequence_id", type: "string" },
        ],
      ),

      // 3. Contact identification HubSpot
      hubspotGetNode(
        "3",
        "Identify Contact in HubSpot CRM",
        520,
        "crm/v3/objects/contacts/{contact_email}?properties=hubspot_owner_id,lifecyclestage,hs_lead_status,num_associated_deals,recent_deal_amount",
        [
          { name: "id", customName: "hubspot_contact_id", type: "string" },
          {
            name: "properties.lifecyclestage",
            customName: "lifecycle_stage",
            type: "string",
          },
          {
            name: "properties.hs_lead_status",
            customName: "lead_status",
            type: "string",
          },
          {
            name: "properties.num_associated_deals",
            customName: "deal_count",
            type: "string",
          },
          {
            name: "properties.recent_deal_amount",
            customName: "deal_value",
            type: "string",
          },
        ],
      ),

      // 4. AI intent classifier
      aiNode(
        "4",
        "AI Classify Engagement Intent",
        760,
        "You are a sales intent classifier. Analyze the engagement event and determine the prospect's likely intent. Classify as: positive_interest (wants to learn more), meeting_request (wants to schedule a call), objection (has concerns but engaged), unsubscribe (wants to opt out), auto_reply (out of office or auto-response), or neutral (passive engagement). Also determine if the outbound sequence should be paused to allow human follow-up. Respond JSON: {intent: string, confidence: number, should_pause: string, pause_reason: string, suggested_action: string, priority: string, sentiment: string}",
        "Engagement type: {engagement_type}. Contact: {contact_name} ({contact_email}). Sequence: {sequence_name} (step {sequence_step}). Content: {engagement_content}. CRM data: lifecycle={lifecycle_stage}, status={lead_status}, deals={deal_count}. Classify the intent and determine if sequence should pause.",
        [
          { field: "intent", as: "classified_intent", type: "string" },
          { field: "confidence", as: "intent_confidence", type: "number" },
          { field: "should_pause", as: "should_pause", type: "string" },
          { field: "pause_reason", as: "pause_reason", type: "string" },
          { field: "suggested_action", as: "suggested_action", type: "string" },
          { field: "priority", as: "response_priority", type: "string" },
          { field: "sentiment", as: "sentiment", type: "string" },
        ],
      ),

      // 5. Pause decision engine condition
      n("5", "condition", "Should Pause Outbound Sequence?", 1000, {
        conditionDetails: {
          conditions: [
            { variable: "should_pause", operator: "eq", value: "yes" },
          ],
        },
      }),

      // 6. Sequence pause executor API (True path)
      n("6", "hitApi", "Pause Outbound Sequence via API", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.outreach.io/api/v2/sequenceStates/{sequence_id}/actions/pause",
          headers: [
            { key: "Authorization", value: "Bearer {outreach_token}" },
            { key: "Content-Type", value: "application/vnd.api+json" },
          ],
          body: [
            {
              key: "data",
              value: '{"type":"sequenceState","attributes":{"state":"paused"}}',
            },
          ],
          variables: [
            {
              name: "data.attributes.state",
              customName: "sequence_state",
              type: "string",
            },
            {
              name: "data.id",
              customName: "sequence_state_id",
              type: "string",
            },
          ],
        },
      }),

      // 7. Sales rep notification Slack
      slackNode(
        "7",
        "Notify Sales Rep of Engagement",
        1480,
        "Prospect Engagement Alert ({response_priority}):\\n*Contact:* {contact_name} ({contact_email})\\n*Event:* {engagement_type} on sequence '{sequence_name}' (step {sequence_step})\\n*Intent:* {classified_intent} ({intent_confidence}% confidence)\\n*Sentiment:* {sentiment}\\n*Sequence:* {should_pause === 'yes' ? 'PAUSED' : 'ACTIVE'} — {pause_reason}\\n*Suggested Action:* {suggested_action}\\n*Content:* {engagement_content}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1385. PDF DOCUMENT Q&A AGENT (8 nodes)
  // ===================================================================

  "PDF Document Q&A Agent": {
    summary:
      "Receives a document upload via webhook, parses the PDF with runCode, chunks the document into searchable segments with runCode, receives questions via a second webhook, retrieves relevant chunks via a vector search API, generates an AI answer with source citations, checks confidence with a condition, and dispatches the answer or escalation to Slack.",
    nodes: [
      trigger("Webhook: PDF document uploaded", "webhook_app", {
        source: "document_portal",
        event: "document.uploaded",
        howItStarts:
          "Fires when a user uploads a PDF document for Q&A, including the document URL, filename, uploader information, and any initial questions to answer about the document",
        setup:
          "Configure webhook from your document portal. Set OpenAI API key for embeddings and answer generation. Configure vector store API for chunk retrieval. Set Slack webhook for answer delivery.",
        inputFields: [],
      }),

      // 2. PDF parser runCode
      n("2", "runCode", "Parse PDF Document Content", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Extract text content from PDF document payload
const payload = JSON.parse(vars.webhook_payload || "{}");
const documentUrl = payload.document_url || payload.url || payload.file_url || "";
const filename = payload.filename || payload.file_name || payload.name || "document.pdf";
const uploaderEmail = payload.uploader_email || payload.email || payload.user_email || "";
const initialQuestion = payload.question || payload.query || payload.initial_question || "";
const documentText = payload.text_content || payload.extracted_text || payload.content || "";
const pageCount = parseInt(payload.page_count || payload.pages || "0", 10);
return {
  document_url: documentUrl,
  document_filename: filename,
  uploader_email: uploaderEmail,
  initial_question: initialQuestion,
  document_text: documentText,
  page_count: pageCount,
  has_text: documentText.length > 50 ? "yes" : "no",
  parsed_at: new Date().toISOString()
};`,
          outputVariables: [
            { name: "document_url", type: "string" },
            { name: "document_filename", type: "string" },
            { name: "uploader_email", type: "string" },
            { name: "initial_question", type: "string" },
            { name: "document_text", type: "string" },
            { name: "page_count", type: "number" },
            { name: "has_text", type: "string" },
            { name: "parsed_at", type: "string" },
          ],
        },
      }),

      // 3. Document chunker runCode
      n("3", "runCode", "Chunk Document into Searchable Segments", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Chunk the document text into overlapping segments for vector search
const text = vars.document_text || "";
const chunkSize = 1000;
const overlap = 200;
const chunks = [];
let start = 0;
while (start < text.length) {
  const end = Math.min(start + chunkSize, text.length);
  const chunk = text.substring(start, end);
  chunks.push({ index: chunks.length, text: chunk, start_char: start, end_char: end });
  start += (chunkSize - overlap);
}
return {
  chunk_count: chunks.length,
  chunks_json: JSON.stringify(chunks),
  avg_chunk_length: chunks.length > 0 ? Math.round(text.length / chunks.length) : 0,
  total_chars: text.length,
  chunking_complete: "yes"
};`,
          outputVariables: [
            { name: "chunk_count", type: "number" },
            { name: "chunks_json", type: "string" },
            { name: "avg_chunk_length", type: "number" },
            { name: "total_chars", type: "number" },
            { name: "chunking_complete", type: "string" },
          ],
        },
      }),

      // 4. Embedding creation for chunks via OpenAI
      n("4", "hitApi", "Create Embeddings for Document Chunks", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.openai.com/v1/embeddings",
          headers: [
            { key: "Authorization", value: "Bearer {openai_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "model", value: "text-embedding-3-small" },
            { key: "input", value: "{initial_question}" },
          ],
          variables: [
            {
              name: "data[0].embedding",
              customName: "question_embedding",
              type: "string",
            },
            {
              name: "usage.total_tokens",
              customName: "embedding_tokens",
              type: "number",
            },
          ],
        },
      }),

      // 5. RAG retrieval — semantic search against chunks
      n("5", "runCode", "Retrieve Most Relevant Document Chunks", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Simple keyword-based retrieval from chunks (simulates vector similarity)
const question = (vars.initial_question || "").toLowerCase();
const chunks = JSON.parse(vars.chunks_json || "[]");
const keywords = question.split(/\\s+/).filter(w => w.length > 3);
let scored = chunks.map(c => {
  const text = (c.text || "").toLowerCase();
  let score = 0;
  for (const kw of keywords) {
    if (text.includes(kw)) score += 1;
  }
  return { ...c, score };
});
scored.sort((a, b) => b.score - a.score);
const topChunks = scored.slice(0, 3);
const context = topChunks.map(c => c.text).join("\\n---\\n");
const bestScore = topChunks[0] ? topChunks[0].score : 0;
return {
  retrieved_context: context,
  chunks_retrieved: topChunks.length,
  best_match_score: bestScore,
  has_relevant_context: bestScore > 0 ? "yes" : "no"
};`,
          outputVariables: [
            { name: "retrieved_context", type: "string" },
            { name: "chunks_retrieved", type: "number" },
            { name: "best_match_score", type: "number" },
            { name: "has_relevant_context", type: "string" },
          ],
        },
      }),

      // 6. AI answer generator
      aiNode(
        "6",
        "AI Generate Answer from Document Context",
        1240,
        "You are a document Q&A assistant. Answer the user's question using ONLY the provided document context. Cite specific sections. If the context doesn't contain enough information to answer, say so clearly. Rate your confidence 0-100. Respond JSON: {answer: string, confidence: number, sources: string, limitations: string, follow_up: string}",
        "Document: {document_filename} ({page_count} pages). Question: {initial_question}. Relevant document sections: {retrieved_context}. Answer the question based strictly on the document content.",
        [
          { field: "answer", as: "generated_answer", type: "string" },
          { field: "confidence", as: "answer_confidence", type: "number" },
          { field: "sources", as: "cited_sources", type: "string" },
          { field: "limitations", as: "answer_limitations", type: "string" },
          { field: "follow_up", as: "follow_up_questions", type: "string" },
        ],
      ),

      // 7. Confidence check condition
      n("7", "condition", "Answer Confidence Above 60%?", 1480, {
        conditionDetails: {
          conditions: [
            { variable: "answer_confidence", operator: "gte", value: "60" },
          ],
        },
      }),

      // 8. Action handler Slack (both paths — answers and escalations)
      slackNode(
        "8",
        "Deliver Answer or Escalation via Slack",
        1720,
        "PDF Q&A — {document_filename}:\\n*Question:* {initial_question}\\n*Answer:* {generated_answer}\\n*Confidence:* {answer_confidence}%\\n*Sources:* {cited_sources}\\n*Limitations:* {answer_limitations}\\n*Follow-up questions:* {follow_up_questions}\\n\\nAsked by: {uploader_email}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
      e("7", "8", "output_2"),
    ],
  },

  // ===================================================================
  // 1386. PO APPROVAL CHAIN WORKFLOW (9 nodes)
  // ===================================================================

  "PO Approval Chain Workflow": {
    summary:
      "Receives a PO submission via webhook, extracts PO data with AI, validates the budget with runCode, checks vendor compliance via API, routes to the appropriate approval tier with a condition, sends the Level 1 approver notification email, advances through approval tiers with a condition, finalizes the PO via API, and logs the complete audit trail to Google Sheets.",
    nodes: [
      trigger("Webhook: Purchase order submitted", "webhook_app", {
        source: "procurement_system",
        event: "po.submitted",
        howItStarts:
          "Fires when a purchase order is submitted for approval, including vendor details, line items, total amount, cost center, budget code, and requested delivery date",
        setup:
          "Configure webhook from your procurement system. Set OpenAI API key for data extraction. Configure budget validation access. Set vendor compliance API endpoint. Configure email for approver notifications. Provide Google Sheets spreadsheet ID for audit trail.",
        inputFields: [],
      }),

      // 2. AI extract PO data
      aiNode(
        "2",
        "AI Extract Purchase Order Details",
        280,
        "You are a procurement data parser. Extract the PO number, vendor name, vendor ID, line items with descriptions and amounts, total amount, cost center, budget code, requester name, requester email, department, delivery date, payment terms, and justification from the raw webhook payload. Respond JSON: {po_number: string, vendor_name: string, vendor_id: string, line_items: string, total_amount: number, cost_center: string, budget_code: string, requester_name: string, requester_email: string, department: string, delivery_date: string, payment_terms: string, justification: string, item_count: number}",
        "Raw webhook payload: {webhook_payload}. Extract all purchase order details regardless of procurement system format.",
        [
          { field: "po_number", as: "po_number", type: "string" },
          { field: "vendor_name", as: "vendor_name", type: "string" },
          { field: "vendor_id", as: "vendor_id", type: "string" },
          { field: "line_items", as: "line_items", type: "string" },
          { field: "total_amount", as: "total_amount", type: "number" },
          { field: "cost_center", as: "cost_center", type: "string" },
          { field: "budget_code", as: "budget_code", type: "string" },
          { field: "requester_name", as: "requester_name", type: "string" },
          { field: "requester_email", as: "requester_email", type: "string" },
          { field: "department", as: "department", type: "string" },
          { field: "delivery_date", as: "delivery_date", type: "string" },
          { field: "payment_terms", as: "payment_terms", type: "string" },
          { field: "justification", as: "justification", type: "string" },
          { field: "item_count", as: "item_count", type: "number" },
        ],
      ),

      // 3. Budget validation runCode
      n("3", "runCode", "Validate Budget Availability", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Check PO amount against budget constraints
const amount = parseFloat(vars.total_amount || "0");
const budgetCode = vars.budget_code || "UNKNOWN";
const dept = (vars.department || "").toLowerCase();
// Budget limits by department (configurable)
const budgetLimits = {
  engineering: 100000,
  marketing: 75000,
  sales: 50000,
  operations: 80000,
  hr: 40000,
  finance: 60000
};
const deptBudget = budgetLimits[dept] || 25000;
const budgetUtilization = Math.round((amount / deptBudget) * 100);
const withinBudget = amount <= deptBudget ? "yes" : "no";
const approvalTier = amount > 50000 ? "executive" : amount > 10000 ? "director" : amount > 2500 ? "manager" : "auto_approve";
const requiresExecutive = approvalTier === "executive" ? "yes" : "no";
return {
  within_budget: withinBudget,
  budget_limit: deptBudget,
  budget_utilization: budgetUtilization,
  approval_tier: approvalTier,
  requires_executive: requiresExecutive,
  budget_remaining: deptBudget - amount,
  validated_at: new Date().toISOString()
};`,
          outputVariables: [
            { name: "within_budget", type: "string" },
            { name: "budget_limit", type: "number" },
            { name: "budget_utilization", type: "number" },
            { name: "approval_tier", type: "string" },
            { name: "requires_executive", type: "string" },
            { name: "budget_remaining", type: "number" },
            { name: "validated_at", type: "string" },
          ],
        },
      }),

      // 4. Vendor compliance check API
      n("4", "hitApi", "Check Vendor Compliance Status", 760, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.hubapi.com/crm/v3/objects/companies/{vendor_id}?properties=compliance_status,insurance_expiry,contract_status,payment_rating",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "properties.compliance_status",
              customName: "vendor_compliance",
              type: "string",
            },
            {
              name: "properties.insurance_expiry",
              customName: "insurance_expiry",
              type: "string",
            },
            {
              name: "properties.contract_status",
              customName: "contract_status",
              type: "string",
            },
            {
              name: "properties.payment_rating",
              customName: "vendor_rating",
              type: "string",
            },
          ],
        },
      }),

      // 5. Approval tier router condition
      n("5", "condition", "Requires Executive Approval?", 1000, {
        conditionDetails: {
          conditions: [
            { variable: "requires_executive", operator: "eq", value: "yes" },
          ],
        },
      }),

      // 6. Level 1 approver notification email
      emailNode(
        "6",
        "Send PO Approval Request to Approver",
        1240,
        "{department_approver_email}",
        "PO Approval Required [{po_number}]: {vendor_name} — ${total_amount}",
        "<h2>Purchase Order Approval Request</h2><p><strong>PO:</strong> {po_number}</p><p><strong>Vendor:</strong> {vendor_name} (ID: {vendor_id})</p><p><strong>Compliance:</strong> {vendor_compliance} | Contract: {contract_status} | Rating: {vendor_rating}</p><hr><p><strong>Items ({item_count}):</strong> {line_items}</p><p><strong>Total:</strong> ${total_amount}</p><p><strong>Cost Center:</strong> {cost_center} | Budget Code: {budget_code}</p><p><strong>Budget Status:</strong> {within_budget === 'yes' ? 'Within Budget' : 'OVER BUDGET'} ({budget_utilization}% utilization)</p><p><strong>Requester:</strong> {requester_name} ({department})</p><p><strong>Justification:</strong> {justification}</p><p><strong>Delivery Date:</strong> {delivery_date}</p><p><strong>Payment Terms:</strong> {payment_terms}</p><p><strong>Approval Tier:</strong> {approval_tier}</p><p>Please reply APPROVE or REJECT with comments.</p>",
      ),

      // 7. Approval advancement condition
      n("7", "condition", "PO Within Budget Limits?", 1480, {
        conditionDetails: {
          conditions: [
            { variable: "within_budget", operator: "eq", value: "yes" },
          ],
        },
      }),

      // 8. PO finalization API (True path — within budget)
      n("8", "hitApi", "Finalize and Issue Purchase Order", 1720, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.hubapi.com/crm/v3/objects/deals",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "properties",
              value:
                '{"dealname":"PO {po_number} — {vendor_name}","amount":"{total_amount}","pipeline":"procurement","dealstage":"approved","description":"{line_items}","closedate":"{delivery_date}"}',
            },
          ],
          variables: [
            { name: "id", customName: "deal_id", type: "string" },
            {
              name: "properties.dealstage",
              customName: "po_stage",
              type: "string",
            },
          ],
        },
      }),

      // 9. Audit trail Sheets
      sheetsNode(
        "9",
        "Log PO Audit Trail to Sheets",
        1960,
        "PO_AuditTrail",
        '[["{validated_at}","{po_number}","{vendor_name}","{vendor_id}","{total_amount}","{cost_center}","{budget_code}","{department}","{requester_name}","{approval_tier}","{within_budget}","{budget_utilization}","{vendor_compliance}","{contract_status}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "6", "output_2"),
      e("6", "7"),
      e("7", "8", "output_1"),
      e("7", "9", "output_2"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 1387. PO APPROVAL WORKFLOW (8 nodes)
  // ===================================================================

  "PO Approval Workflow": {
    summary:
      "Receives a PO creation event via webhook, extracts PO data with AI, performs three-way match validation (PO, receipt, invoice) with runCode, routes based on match status with a condition, sends an approver notification email, evaluates the approval decision with a condition, dispatches the approved PO via API, and links receipt and payment records in Google Sheets.",
    nodes: [
      trigger("Webhook: Purchase order created", "webhook_app", {
        source: "accounting_system",
        event: "po.created",
        howItStarts:
          "Fires when a new purchase order is created in the accounting system, including PO details, related invoice reference, goods receipt reference, and vendor information for three-way match validation",
        setup:
          "Configure webhook from your accounting system (NetSuite, QuickBooks, SAP). Set OpenAI API key for data extraction. Configure email for approver notifications. Set API endpoint for PO dispatch. Provide Google Sheets spreadsheet ID for tracking.",
        inputFields: [],
      }),

      // 2. AI extract PO data
      aiNode(
        "2",
        "AI Extract PO, Invoice, and Receipt Data",
        280,
        "You are an accounts payable data parser. Extract the PO number, vendor name, vendor email, PO line items with quantities and unit prices, PO total, invoice number, invoice total, invoice date, receipt number, received quantities, receipt date, variance tolerance, requester name, approver email, and payment terms from the raw webhook payload. Respond JSON: {po_number: string, vendor_name: string, vendor_email: string, po_items: string, po_total: number, invoice_number: string, invoice_total: number, invoice_date: string, receipt_number: string, received_quantities: string, receipt_date: string, variance_tolerance: number, requester_name: string, approver_email: string, payment_terms: string}",
        "Raw webhook payload: {webhook_payload}. Extract all PO, invoice, and receipt details regardless of accounting system format.",
        [
          { field: "po_number", as: "po_number", type: "string" },
          { field: "vendor_name", as: "vendor_name", type: "string" },
          { field: "vendor_email", as: "vendor_email", type: "string" },
          { field: "po_items", as: "po_items", type: "string" },
          { field: "po_total", as: "po_total", type: "number" },
          { field: "invoice_number", as: "invoice_number", type: "string" },
          { field: "invoice_total", as: "invoice_total", type: "number" },
          { field: "invoice_date", as: "invoice_date", type: "string" },
          { field: "receipt_number", as: "receipt_number", type: "string" },
          {
            field: "received_quantities",
            as: "received_quantities",
            type: "string",
          },
          { field: "receipt_date", as: "receipt_date", type: "string" },
          {
            field: "variance_tolerance",
            as: "variance_tolerance",
            type: "number",
          },
          { field: "requester_name", as: "requester_name", type: "string" },
          { field: "approver_email", as: "approver_email", type: "string" },
          { field: "payment_terms", as: "payment_terms", type: "string" },
        ],
      ),

      // 3. Three-way match validator runCode
      n("3", "runCode", "Perform Three-Way Match Validation", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Three-way match: PO vs Invoice vs Receipt
const poTotal = parseFloat(vars.po_total || "0");
const invoiceTotal = parseFloat(vars.invoice_total || "0");
const tolerance = parseFloat(vars.variance_tolerance || "2") / 100;
const hasInvoice = (vars.invoice_number || "").length > 0;
const hasReceipt = (vars.receipt_number || "").length > 0;
// Price match: PO total vs Invoice total
const priceVariance = poTotal > 0 ? Math.abs(poTotal - invoiceTotal) / poTotal : 0;
const priceMatch = priceVariance <= tolerance ? "MATCH" : "MISMATCH";
// Quantity match: compare PO items vs received quantities
const poItems = (vars.po_items || "").split(",").length;
const rcvItems = (vars.received_quantities || "").split(",").length;
const qtyMatch = poItems === rcvItems ? "MATCH" : "MISMATCH";
// Overall three-way match
const allMatch = priceMatch === "MATCH" && qtyMatch === "MATCH" && hasInvoice && hasReceipt;
const matchStatus = allMatch ? "FULL_MATCH" : (!hasInvoice || !hasReceipt) ? "PARTIAL" : "MISMATCH";
const autoApprovable = allMatch && poTotal <= 10000 ? "yes" : "no";
const varianceAmount = Math.abs(poTotal - invoiceTotal).toFixed(2);
return {
  price_match: priceMatch,
  quantity_match: qtyMatch,
  match_status: matchStatus,
  price_variance_pct: (priceVariance * 100).toFixed(1),
  variance_amount: varianceAmount,
  has_invoice: hasInvoice ? "yes" : "no",
  has_receipt: hasReceipt ? "yes" : "no",
  auto_approvable: autoApprovable,
  validation_timestamp: new Date().toISOString()
};`,
          outputVariables: [
            { name: "price_match", type: "string" },
            { name: "quantity_match", type: "string" },
            { name: "match_status", type: "string" },
            { name: "price_variance_pct", type: "string" },
            { name: "variance_amount", type: "string" },
            { name: "has_invoice", type: "string" },
            { name: "has_receipt", type: "string" },
            { name: "auto_approvable", type: "string" },
            { name: "validation_timestamp", type: "string" },
          ],
        },
      }),

      // 4. Approval routing condition
      n("4", "condition", "Three-Way Match Passed?", 760, {
        conditionDetails: {
          conditions: [
            { variable: "match_status", operator: "eq", value: "FULL_MATCH" },
          ],
        },
      }),

      // 5. Approver notification email
      emailNode(
        "5",
        "Send PO for Approval",
        1000,
        "{approver_email}",
        "PO Approval [{po_number}]: {vendor_name} — ${po_total} ({match_status})",
        "<h2>Purchase Order Approval Request</h2><p><strong>PO:</strong> {po_number}</p><p><strong>Vendor:</strong> {vendor_name}</p><p><strong>PO Total:</strong> ${po_total}</p><hr><h3>Three-Way Match Results</h3><ul><li><strong>Price Match:</strong> {price_match} (Variance: {price_variance_pct}%, ${variance_amount})</li><li><strong>Quantity Match:</strong> {quantity_match}</li><li><strong>Overall Status:</strong> {match_status}</li><li><strong>Invoice:</strong> {invoice_number} — ${invoice_total} ({has_invoice})</li><li><strong>Receipt:</strong> {receipt_number} ({has_receipt})</li></ul><h3>PO Details</h3><p><strong>Items:</strong> {po_items}</p><p><strong>Received:</strong> {received_quantities}</p><p><strong>Payment Terms:</strong> {payment_terms}</p><p><strong>Requester:</strong> {requester_name}</p><p>Please reply APPROVE or REJECT with comments.</p>",
      ),

      // 6. Approval decision condition
      n(
        "6",
        "condition",
        "PO Auto-Approvable (Full Match + Under $10k)?",
        1240,
        {
          conditionDetails: {
            conditions: [
              { variable: "auto_approvable", operator: "eq", value: "yes" },
            ],
          },
        },
      ),

      // 7. Approved PO dispatcher API (True path)
      n("7", "hitApi", "Dispatch Approved PO for Payment", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.hubapi.com/crm/v3/objects/deals",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "properties",
              value:
                '{"dealname":"PO {po_number} — {vendor_name}","amount":"{po_total}","pipeline":"accounts_payable","dealstage":"approved_for_payment","description":"Invoice: {invoice_number}. Receipt: {receipt_number}. Match: {match_status}. Terms: {payment_terms}"}',
            },
          ],
          variables: [
            { name: "id", customName: "payment_deal_id", type: "string" },
            {
              name: "properties.dealstage",
              customName: "payment_stage",
              type: "string",
            },
          ],
        },
      }),

      // 8. Receipt payment linkage Sheets
      sheetsNode(
        "8",
        "Log PO Match and Payment Linkage",
        1720,
        "PO_Tracking",
        '[["{validation_timestamp}","{po_number}","{vendor_name}","{po_total}","{invoice_number}","{invoice_total}","{receipt_number}","{match_status}","{price_match}","{quantity_match}","{price_variance_pct}","{auto_approvable}","{payment_terms}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5", "output_2"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("6", "8", "output_2"),
      e("7", "8"),
    ],
  },
};
